import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation, WritableSignal, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { User } from '@microsoft/microsoft-graph-types';
import { BehaviorSubject, EMPTY, Observable, Subscription, catchError, finalize, first, firstValueFrom, forkJoin, map, of, switchMap, tap, throwError } from 'rxjs';
import { DownloadService } from 'src/app/core/services/download.service';
import { GraphService } from 'src/app/core/services/graph.service';
import { indicate } from 'src/app/operators';
import { environment } from 'src/environments/environment';
import { PipingAndValvesDocumentService } from '../../piping-valves/services/piping-and-valves-document.service';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { CustomAlertDialogComponent } from '../../shared/custom-alert-dialog/custom-alert-dialog.component';
import {
  ApiException,
  ApprovalState,
  BranchTableViewModel,
  CommonNoteViewModel,
  ComponentValidationResult,
  ComponentViewModel,
  ComponentsInPipeClassViewModel,
  DesignCodeViewModel,
  EsraPipingAPIClient,
  PipeClassNoteViewModel,
  PipeClassViewModel,
  PipingRevisionViewModel,
  PressureTemperatureTableNoteViewModel,
  RatingClassViewModel,
  ReleaseObjectGroupRoleViewModel,
  ReleaseObjectGroupViewModel,
  RevisionStatus,
  RoleViewModel,
  ShareLinkResponse,
  UserType,
  ValveValidationResult,
  ValveViewModel
} from '../../shared/models/autogenerated-piping';
import { CommentAuthorRole, CommentViewModel } from '../../shared/models/comment.model';
import { CommentsService, SaveAllCommentsRequest } from '../../shared/services/comments.service';
import { NpsValidationService } from '../../shared/services/nps-validation.service';
import { ReleaseLifecycleService } from '../../shared/services/release-lifecycle.service';
import { getApiErrorMessage } from '../../shared/utils';
import { BranchDictionaryModalComponent, BranchDictionaryModalData } from '../branch-tables/branch-dictionary-modal/branch-dictionary-modal.component';
import { BranchTablesComponent } from '../branch-tables/branch-tables.component';
import { ClonePipeClassDialogComponent, ClonePipeClassDialogData } from '../clone-pipe-class-dialog/clone-pipe-class-dialog.component';
import { GeneralDetailsComponent } from '../general-details/general-details.component';
import { ComponentCatalogModalComponent, ComponentCatalogSelectionData, ComponentOption } from '../piping-summary/component-catalog-modal/component-catalog-modal.component';
import { NotesSelectionModalComponent } from '../piping-summary/notes-selection-modal/notes-selection-modal.component';
import { PipingSummaryComponent } from '../piping-summary/piping-summary.component';
import { ValveSelectionModalComponent, ValveSelectionModalData } from '../valve-selection-modal/valve-selection-modal.component';

// Interface for child components to communicate data changes
export interface PipeClassDataChange {
  field: string;
  value: any;
  section: 'summary' | 'ptt' | 'notes' | 'serviceDetails' | 'comments' | 'components' | 'valves' | 'general-details';
}

@Component({
    selector: 'app-piping-tabs-parent-component',
    styleUrls: ['piping-tabs-parent.component.scss'],
    templateUrl: 'piping-tabs-parent.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PipingTabsParentComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() pipeClassId: string;
  @Input() inputPipeClass: PipeClassViewModel;
  @Input() isEditMode: boolean;
  @Output() closePipingPage = new EventEmitter<boolean>();

  // ViewChild reference to general details component
  @ViewChild('generalDetailsComponent') generalDetailsComponent: GeneralDetailsComponent;

  // ViewChild reference to piping summary component
  @ViewChild('pipingSummaryComponent') pipingSummaryComponent: PipingSummaryComponent;

  // ViewChild reference to branch tables component
  @ViewChild('branchTablesComponent') branchTablesComponent: BranchTablesComponent;

  openState = true;

  pipeClassViewModel$: Observable<PipeClassViewModel>;
  pipeClassName: string;
  businessUnit: string;

  // Permission validation - centralized for all child components
  canEdit$: Observable<boolean>;
  private canEditSubject = new BehaviorSubject<boolean>(false);

  /** True when the user arrived via the revisions dashboard (controls back-button label/destination),
   *  regardless of whether they are allowed to edit fields. */
  isInRevisionWorkflow: boolean = false;

  /**
   * The URL to navigate back to when the user clicks the return button.
   * Set from `history.state.returnUrl` on load. `null` means no explicit origin was provided
   * (e.g. the user arrived directly from search or a bookmark) — `navigateBack()` falls back
   * to admin search in that case.
   */
  private returnUrl: string | null = null;

  // Current pipe class data for API operations
  private currentPipeClassViewModel: PipeClassViewModel | null = null;

  serviceDisplayName = '';
  branchTableViewModelList: BranchTableViewModel[] = [];
  exportIndicator$ = new BehaviorSubject<boolean>(false);
  loadingIndicator$ = new BehaviorSubject<boolean>(false);

  /** Why the pipe class could not be shown (no access, not found, ...); replaces the loading skeleton. */
  readonly loadErrorMessage: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
  savingIndicator$ = new BehaviorSubject<boolean>(false);
  graphService$ = new Observable<User>();
  userEmail = new BehaviorSubject<string>('');
  userEmail$ = this.userEmail.asObservable();
  authorName = new BehaviorSubject<string>('Unknown Author');
  authorName$ = this.authorName.asObservable();

  /** Resolved display name for the author of the previous revision — shown in compare mode. */
  previousAuthorName = new BehaviorSubject<string>('');
  previousAuthorName$ = this.previousAuthorName.asObservable();
  userId = new BehaviorSubject<string>('');
  userId$ = this.userId.asObservable();

  isProduction = environment.production;
  faChevronLeft = faChevronLeft;

  // Data change tracking for child components
  private pendingChanges: Map<string, any> = new Map();
  // Bump this whenever commonNotes array gains new unique entries to trigger OnChanges in consumers
  public commonNotesVersion = 0;

  // Review mode state
  isReviewMode = false;
  isCompareMode = false;
  previousPipeClassViewModel: PipeClassViewModel | null = null;
  compareLoadingIndicator$ = new BehaviorSubject<boolean>(false);
  currentUserIsReviewer = false;
  // True when the current reviewer is specifically an INTERNAL_REVIEWER (UserType._2).
  // Internal reviewers use "No Comments"/"Commented" actions instead of Approve/Reject.
  currentUserIsInternalReviewer = false;
  currentUserIsOwner = false;
  currentUserApprovalState: ApprovalState | null = null;
  revisionMembers: RoleViewModel[] = [];
  approvingIndicator$ = new BehaviorSubject<boolean>(false);

  // Release context state (when pipe class is part of a release)
  isInReleaseContext = false;
  releaseRoles: ReleaseObjectGroupRoleViewModel[] = [];
  releaseCurrentUserApprovalState: ApprovalState | null = null;
  releaseCurrentUserIsReviewer = false;
  releaseCurrentUserIsInternalReviewer = false;

  /**
   * Release reviewer list deduplicated by `userId`, following the same rules as `ReviewerFilterPipe`:
   * - Only INTERNAL_REVIEWER (2) and EXTERNAL_REVIEWER (3) roles are included.
   * - Pre-sorted internal-first so internal wins when the same user appears on both lists.
   * - Deduplicated by `userId` (first occurrence after sort = internal role).
   * - Final sort: internal first, then alphabetical by display name.
   */
  get uniqueReleaseReviewers(): ReleaseObjectGroupRoleViewModel[] {
    // Keep reviewers only, sort internal (2) before external (3) so internal wins dedup
    const reviewersOnly = this.releaseRoles
      .filter((r: ReleaseObjectGroupRoleViewModel) => r.userRole === UserType._2 || r.userRole === UserType._3)
      .sort((a: ReleaseObjectGroupRoleViewModel, b: ReleaseObjectGroupRoleViewModel) =>
        (a.userRole ?? 9) - (b.userRole ?? 9)
      );

    // Deduplicate: first occurrence per userId wins (internal, due to sort above)
    const seen = new Set<string>();
    const unique = reviewersOnly.filter((r: ReleaseObjectGroupRoleViewModel) => {
      if (!r.userId || seen.has(r.userId)) { return false; }
      seen.add(r.userId);
      return true;
    });

    // Final sort: internal first, then alphabetical by display name
    return unique.sort((a: ReleaseObjectGroupRoleViewModel, b: ReleaseObjectGroupRoleViewModel) => {
      if (a.userRole !== b.userRole) { return (a.userRole ?? 9) - (b.userRole ?? 9); }
      return (a.userDisplayName ?? '').toLowerCase().localeCompare((b.userDisplayName ?? '').toLowerCase());
    });
  }

  /** Current user's approval state for the active context (release when in release, otherwise revision). */
  get activeApprovalState(): ApprovalState | null {
    return this.isInReleaseContext ? this.releaseCurrentUserApprovalState : this.currentUserApprovalState;
  }

  /** True when the active-context reviewer is an internal reviewer (UserType._2). */
  get activeUserIsInternalReviewer(): boolean {
    return this.isInReleaseContext ? this.releaseCurrentUserIsInternalReviewer : this.currentUserIsInternalReviewer;
  }

  /**
   * True when at least one comment (in any tab) was authored by a reviewer.
   * A comment counts if its author holds an internal/external reviewer role on this
   * revision/release, even when the comment's stored `authorRole` reflects a different
   * "main" role (a user can be both e.g. Author and internal reviewer on the same revision).
   * Gates the internal reviewer's "Commented" action.
   */
  get hasReviewerComment(): boolean {
    const reviewerUserIds: Set<string> = this.getReviewerUserIds();
    return this.anyReviewerComment(this.summaryComments, reviewerUserIds)
      || this.anyReviewerComment(this.componentComments, reviewerUserIds)
      || this.anyReviewerComment(this.valveComments, reviewerUserIds)
      || this.anyReviewerComment(this.branchTableComments, reviewerUserIds);
  }

  /** True when the internal reviewer's "Commented" action must be blocked (no reviewer comment yet). */
  get commentedActionDisabled(): boolean {
    return this.activeUserIsInternalReviewer && !this.hasReviewerComment;
  }

  /** Approve-side button label: internal reviewers see "No Comments" instead of "Approve". */
  get reviewerApproveLabel(): string {
    return this.activeUserIsInternalReviewer ? 'No Comments' : 'Approve';
  }

  /** Reject-side button label: internal reviewers see "Commented" instead of "Reject". */
  get reviewerRejectLabel(): string {
    return this.activeUserIsInternalReviewer ? 'Commented' : 'Reject';
  }

  /**
   * Approval-state badge label for a specific reviewer row (owner/release lists).
   * Internal reviewers (UserType._2) see "No Comments"/"Commented" instead of "Approved"/"Rejected".
   */
  /**
   * Human-readable label for an approval state. The backend now models internal-reviewer
   * decisions as distinct states, so no per-role relabeling is needed: Approved/Rejected are
   * external-reviewer decisions; NoComments/Commented are internal-reviewer decisions.
   */
  approvalStateLabel(approvalState: ApprovalState | null | undefined): string {
    switch (approvalState) {
      case ApprovalState._1: return 'Approved';
      case ApprovalState._2: return 'Rejected';
      case ApprovalState._3: return 'No Comments';
      case ApprovalState._4: return 'Commented';
      default: return 'Pending';
    }
  }

  /** CSS badge class for an approval state. NoComments reuses approved (green); Commented reuses rejected (red). */
  approvalBadgeClass(approvalState: ApprovalState | null | undefined): string {
    switch (approvalState) {
      case ApprovalState._1: // Approved
      case ApprovalState._3: // NoComments
        return 'approval-approved';
      case ApprovalState._2: // Rejected
      case ApprovalState._4: // Commented
        return 'approval-rejected';
      default:
        return 'approval-pending';
    }
  }

  /** True when the active reviewer has not recorded a decision yet (Pending or none). */
  get activeStateIsPending(): boolean {
    return this.activeApprovalState == null || this.activeApprovalState === ApprovalState._0;
  }

  /** True when the active reviewer recorded a positive decision (Approved or No Comments). */
  get activeStateIsPositive(): boolean {
    return this.activeApprovalState === ApprovalState._1 || this.activeApprovalState === ApprovalState._3;
  }

  /** True when the active reviewer recorded a negative/attention decision (Rejected or Commented). */
  get activeStateIsNegative(): boolean {
    return this.activeApprovalState === ApprovalState._2 || this.activeApprovalState === ApprovalState._4;
  }

  /** Current-state badge label. */
  get reviewerStateBadgeLabel(): string {
    return this.approvalStateLabel(this.activeApprovalState);
  }

  /** Current-state badge icon; "Commented" uses a comment glyph rather than a cancel glyph. */
  get reviewerStateBadgeIcon(): string {
    switch (this.activeApprovalState) {
      case ApprovalState._1: // Approved
      case ApprovalState._3: // NoComments
        return 'check_circle';
      case ApprovalState._2: // Rejected
        return 'cancel';
      case ApprovalState._4: // Commented
        return 'comment';
      default:
        return 'pending';
    }
  }

  /**
   * Collects the OIDs of every user holding a reviewer role (INTERNAL_REVIEWER or
   * EXTERNAL_REVIEWER) on the active context. A single user may appear on multiple role
   * rows, so this captures reviewer membership regardless of the user's other roles.
   */
  private getReviewerUserIds(): Set<string> {
    const reviewerUserIds: Set<string> = new Set<string>();
    const roles: Array<RoleViewModel | ReleaseObjectGroupRoleViewModel> =
      this.isInReleaseContext ? this.releaseRoles : this.revisionMembers;
    for (const role of roles ?? []) {
      if ((role.userRole === UserType._2 || role.userRole === UserType._3) && role.userId) {
        reviewerUserIds.add(role.userId);
      }
    }
    return reviewerUserIds;
  }

  /**
   * Recursively determines whether any comment in the given tree was authored by a reviewer.
   * A comment qualifies when its author holds a reviewer role on the revision/release
   * (`reviewerUserIds`) or its stored `authorRole` is a reviewer role. Replies are stored as
   * nested `CommentViewModel` arrays, so the whole thread must be walked.
   */
  private anyReviewerComment(comments: CommentViewModel[], reviewerUserIds: Set<string>): boolean {
    for (const comment of comments ?? []) {
      const authoredByReviewerRole: boolean = comment.authorRole === CommentAuthorRole.InternalReviewer
        || comment.authorRole === CommentAuthorRole.ExternalReviewer;
      const authorIsReviewerMember: boolean = !!comment.authorId && reviewerUserIds.has(comment.authorId);
      if (authoredByReviewerRole || authorIsReviewerMember) {
        return true;
      }
      if (comment.replies?.length && this.anyReviewerComment(comment.replies, reviewerUserIds)) {
        return true;
      }
    }
    return false;
  }

  // Comments management - per tab
  summaryComments: CommentViewModel[] = [];
  componentComments: CommentViewModel[] = [];
  valveComments: CommentViewModel[] = [];
  branchTableComments: CommentViewModel[] = [];

  // Comments loading states
  commentsLoading$ = new BehaviorSubject<boolean>(false);
  commentsSaving$ = new BehaviorSubject<boolean>(false);

  // ── Catalog validation ───────────────────────────────────────────────────
  // Populated after the pipe class loads (DRAFT revisions only) by a single
  // batch call per entity type.  Keyed by component.componentId / valve.id.
  /** Validation results for components, keyed by ComponentViewModel.componentId. */
  componentValidationResults: Map<string, ComponentValidationResult> = new Map();
  /** Validation results for valves, keyed by ValveViewModel.id. */
  valveValidationResults: Map<string, ValveValidationResult> = new Map();
  /** True while validateCatalogFreshness's batch requests are in flight. Gates the tab
   *  content so links relying on `valveValidationResults` (e.g. the valve tag) never render
   *  before the data they depend on is available. */
  catalogValidationLoading$ = new BehaviorSubject<boolean>(false);

  // Subscription management
  private routerEventsSubscription: Subscription;
  private routeParamsSubscription: Subscription;

  private orderedComponentGroups = [
        'Pipe',
        'Nipples/Swages',
        'Fittings',
        'Tube Fittings',
        'Flanges',
        'Blanks and Spacers',
        'Gaskets',
        'Bolts',
        'MISC',
        'Valves'
    ];
  private readonly _npsValidationService = inject(NpsValidationService);
  private readonly releaseLifecycleService: ReleaseLifecycleService = inject(ReleaseLifecycleService);

  /** Release object group of the loaded pipe class (kept current after saves); undefined outside a release. */
  get releaseObjectGroup(): ReleaseObjectGroupViewModel | undefined {
    return this.currentPipeClassViewModel?.releaseObjectGroup;
  }

  /** True when saving will first move this pipe class from "release as is" to "revise". */
  private get requiresReviseOnSave(): boolean {
    return this.releaseLifecycleService.requiresReviseOnSave(this.releaseObjectGroup);
  }

  /** True when another tab changed this pipe class's life cycle after the page loaded; saving is blocked until it is reloaded. */
  get isLifecycleOutdated(): boolean {
    return this.releaseLifecycleService.isOutdated(this.releaseObjectGroup);
  }

  /** Tells the user to reload and returns true when saving must not go ahead. */
  private blockSaveIfLifecycleOutdated(): boolean {
    if (!this.isLifecycleOutdated) { return false; }

    this.dialog.open(CustomAlertDialogComponent, {
      width: '450px',
      data: {
        message: 'The life cycle of this item was changed in another tab. Reload the page before saving.',
        buttonText: 'okay'
      }
    });
    return true;
  }

  /**
   * Refetches the pipe class and tells the release page its review status and approvals, so the release table
   * follows a status change made here. No-op outside a release.
   * @param onDone Runs once the announcement is sent (or skipped/failed), e.g. before a page reload.
   */
  private announceReviewChange(onDone?: () => void): void {
    if (!this.isInReleaseContext || !this.pipeClassId) {
      onDone?.();
      return;
    }

    this.esraPipingAPIClient.getPipeClassByPipeClassId(this.pipeClassId).pipe(first()).subscribe({
      next: (fresh: PipeClassViewModel) => {
        if (fresh.releaseObjectGroup) {
          this.releaseLifecycleService.announceChange(fresh.releaseObjectGroup);
        }
        onDone?.();
      },
      error: () => onDone?.()
    });
  }

  /** Enables editing unless the release life cycle locks this pipe class. */
  private grantEditAccess(): void {
    if (!this.releaseLifecycleService.isEditLocked(this.releaseObjectGroup)) {
      this.canEditSubject.next(true);
    }
  }

  /** Tells the release page when a save moved this pipe class to a different lifecycle state. */
  private announceLifecycleChange(before: ReleaseObjectGroupViewModel | undefined, after: ReleaseObjectGroupViewModel | undefined): void {
    if (after && this.releaseLifecycleService.hasLifecycleChanged(before, after)) {
      this.releaseLifecycleService.announceChange(after);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private esraPipingAPIClient: EsraPipingAPIClient,
    private downloadService: DownloadService,
    private graphService: GraphService,
    private pipingAndValvesDocumentService: PipingAndValvesDocumentService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private commentsService: CommentsService,
    private npsValidationService: NpsValidationService,
    private msalService: MsalService
  ) {
    // Listen to all router events to detect navigation away from edit mode
    this.subscribeToRouterEvents();
  }

  ngOnInit() {
    // Initialize all properties synchronously from route snapshot to prevent ExpressionChangedAfterItHasBeenCheckedError
    const initialParams = this.route.snapshot.paramMap;

    // Set all route-based properties immediately
    this.pipeClassName = initialParams.get('name');
    this.businessUnit = initialParams.get('businessUnit');
    this.isEditMode = initialParams.get('isEditMode') === 'true';

    // Initialize permissions - centralized for all child components
    this.initializePermissions();

    // Set pipe class ID
    if (this.pipeClassName == null && this.pipeClassId == null) {
      this.pipeClassId = initialParams.get('id');
      this.pipeClassName = this.pipeClassId;
    }

    const initPipeClassId = this.pipeClassId || '';

    // Force change detection to ensure all bindings are updated
    this.cdr.detectChanges();

    // Subscribe to future route changes after initial setup
    setTimeout(() => {
      this.subscribeToRouteChanges();
      this.initializeAsyncOperations(initPipeClassId);
    }, 0);
  }

  private initializePermissions(): void {
    // Check if user came from the revisions dashboard
    const cameFromRevisionsDashboard = this.checkIfCameFromRevisionsDashboard();

    // Track workflow origin separately from edit permissions so the back button
    // always shows "return to revisions" for all users (including reviewers) who
    // arrived from the revisions dashboard — even after canEdit$ is revoked for reviewers.
    this.isInRevisionWorkflow = cameFromRevisionsDashboard;

    // Always start with editing disabled regardless of URL params.
    // resolveEditPermissions() restores access only after the pipe class loads
    // and the user's role is confirmed via the API. This eliminates the race-condition
    // window where a reviewer could see edit controls before the role check completes.
    this.canEditSubject.next(false);

    this.canEdit$ = this.canEditSubject.asObservable();
  }

  /**
   * Determines whether the user arrived via a workflow entry point and captures the
   * return URL for the back button.
   *
   * PRIMARY mechanism: Angular Router navigation state, read from two sources in order:
   * 1. `getCurrentNavigation().extras.state.returnUrl` — authoritative during `ngOnInit`
   *    because the navigation is still in progress and the extras have not yet been
   *    flushed. Null once navigation completes (e.g. when called from subscribeToRouteChanges).
   * 2. `history.state.returnUrl` — reliable fallback; Angular persists `extras.state` into
   *    `history.state` via pushState so the value survives after navigation ends.
   *
   * `this.returnUrl` is ALWAYS reset to `null` at the start so that stale values from a
   * previous navigation cannot bleed through when this function is re-entered (e.g. the
   * immediate emission from `paramMap.subscribe` in subscribeToRouteChanges can trigger
   * a second call with the same history state).
   */
  private checkIfCameFromRevisionsDashboard(): boolean {
    const currentUrl = this.router.url;

    // Always start from a clean slate so repeated calls do not inherit stale return URLs.
    this.returnUrl = null;

    // Read returnUrl from the current navigation extras (most reliable during ngOnInit)
    // then fall back to history.state (reliable after navigation has completed).
    const currentNavState = this.router.getCurrentNavigation()?.extras?.state as Record<string, unknown> | undefined;
    const stateReturnUrl = (
      currentNavState?.['returnUrl'] ?? (history.state as Record<string, unknown>)?.['returnUrl']
    ) as string | undefined;

    if (stateReturnUrl) {
      this.returnUrl = stateReturnUrl;
    } else {
      // LEGACY FALLBACK: support callers that still write a named context to session storage.
      // Map to the real URL so navigateBack() can always use this.returnUrl directly.
      const storedContext = sessionStorage.getItem('pipingNavigationContext');
      const legacyUrlMap: Record<string, string> = {
        myWorkspace: '/myworkspace',
        revisionsAndReleases: '/piping/revisionsAndReleases',
        adminSearch: '/piping/adminSearch'
      };
      if (storedContext && legacyUrlMap[storedContext]) {
        this.returnUrl = legacyUrlMap[storedContext];
        sessionStorage.removeItem('pipingNavigationContext');
      }
    }

    // If isEditMode=true is in the URL the user is in an edit workflow — check permissions.
    const hasEditModeInUrl = currentUrl.includes('isEditMode=true') ||
                            this.route.snapshot.paramMap.get('isEditMode') === 'true';
    if (hasEditModeInUrl) {
      return true;
    }

    // If a return URL was explicitly captured this navigation, the user arrived from a
    // known screen and is in a revision/edit workflow.
    if (this.returnUrl !== null) {
      return true;
    }

    return false;
  }

  /**
   * Subscribe to router events to detect navigation and enforce permission control
   * Only disables edit mode if user navigates to routes that don't have explicit edit mode
   */
  private subscribeToRouterEvents(): void {
    this.routerEventsSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const currentUrl = event.url;

        // Only force read-only mode if navigating to routes without explicit edit mode
        if ((currentUrl.includes('/piping/search') || currentUrl.includes('/admin')) &&
            !currentUrl.includes('isEditMode=true')) {

              // Force disable edit mode
          this.canEditSubject.next(false);

          // Clear any session context that might grant edit permissions
          sessionStorage.removeItem('pipingNavigationContext');

          // Force change detection
          this.cdr.markForCheck();
        }
      }
    });
  }

  private subscribeToRouteChanges() {
    this.routeParamsSubscription = this.route.paramMap.subscribe(params => {
      const newPipeClassName = params.get('name');
      const newBusinessUnit = params.get('businessUnit');
      const newIsEditMode = params.get('isEditMode') === 'true';

      // Only update if values have actually changed
      if (newPipeClassName !== this.pipeClassName ||
          newBusinessUnit !== this.businessUnit ||
          newIsEditMode !== this.isEditMode) {

        this.pipeClassName = newPipeClassName;
        this.businessUnit = newBusinessUnit;
        this.isEditMode = newIsEditMode;

        // Re-evaluate permissions on route change to ensure strictness
        this.initializePermissions();

        // Use markForCheck instead of detectChanges for OnPush components
        this.cdr.markForCheck();
      }
    });
  }

  ngAfterViewInit() {
    // AfterViewInit is kept for any future view-related initialization
    // Force a final change detection after view is initialized
    this.cdr.markForCheck();
  }

  private initializeAsyncOperations(initPipeClassId: string) {
    if (initPipeClassId) {
      this.getPipeClassById(initPipeClassId);
    }

    if (initPipeClassId || this.pipeClassName) {
        this.getPipeClassDetails(initPipeClassId, this.pipeClassName, this.businessUnit);
    }
  }

  private getPipeClassDetails(
    pipeClassId: string,
    pipeClassName: string,
    businessUnit: string
  ) {
    if (pipeClassId) {
      this.getPipeClassByName(pipeClassId, '');
    } else {
      if (businessUnit == null) {
        this.getPipeClassByName(pipeClassName, '');
      } else {
        this.getPipeClassByName(pipeClassName, businessUnit);
      }
    }
  }

  private getPipeClassByName(
    pipeClassName: string,
    businessUnit: string | undefined
  ): void {
    this.pipeClassViewModel$ = this.esraPipingAPIClient
      .getPipeClassByName(pipeClassName, businessUnit)
      .pipe(
        indicate(this.loadingIndicator$),
        first(),
        catchError((error: unknown) => this.showLoadError(error)),
        tap((pipeClass) => {
          this.loadPipeClass(pipeClass);
        })
      );
  }

  private getPipeClassById(pipeClassId: string): void {
    this.pipeClassViewModel$ = this.esraPipingAPIClient
      .getPipeClassByPipeClassId(pipeClassId)
      .pipe(
        indicate(this.loadingIndicator$),
        first(),
        catchError((error: unknown) => this.showLoadError(error)),
        tap((pipeClass) => {
          this.loadPipeClass(pipeClass);
        })
      );
  }

  /** Puts the failure on the page and completes without a value, so the skeleton gives way to the message. */
  private showLoadError(error: unknown): Observable<never> {
    const message: string = ApiException.isApiException(error) && error.status === 404
      ? 'The pipe class could not be found. It may have been removed, or the link may be incorrect.'
      : getApiErrorMessage(error, 'The pipe class could not be loaded. Please try again later.');
    this.loadErrorMessage.set(message);
    return EMPTY;
  }

  private loadPipeClass(pipeClass: PipeClassViewModel) {
    // Debug: Log what data comes from backend on load/refresh
    console.log('[loadPipeClass] Loading pipe class - Current revision:', pipeClass.fromRevision?.name);
    if (pipeClass.valves && pipeClass.valves.length > 0) {
      console.log('[loadPipeClass] Valves from backend:', pipeClass.valves.map(v => ({
        tag: v.tag,
        fromRevisionName: v.fromRevision?.name
      })));
    }
    if (pipeClass.componentsInPipeClass && pipeClass.componentsInPipeClass.length > 0) {
      console.log('[loadPipeClass] Components from backend:', pipeClass.componentsInPipeClass.slice(0, 5).map(c => ({
        type: c.component?.type,
        lastUpdatedRevision: c.lastUpdatedRevision
      })));
    }

    this.branchTableViewModelList = pipeClass.branchTables;
    this.pipeClassId = pipeClass.id;

    // The pipe class endpoint omits branch table cells to keep the payload small.
    // Fetch and patch them in so the table graphic renders correctly after a page refresh.
    this.enrichBranchTableCells();

    // Store current pipe class data for API operations
    this.currentPipeClassViewModel = pipeClass;

    // Normalize properties to ensure correct structure for API
    this.normalizePropertiesStructure(this.currentPipeClassViewModel);

    // Initialize independent notes for each component to prevent shared references
    this.initializeIndependentComponentNotes();
    this.ensureValveDataConsistency();

    // Initial global union of notes (summary already in pipeClass.commonNotes, add components & valves unique)
    try {
      this.unifyAllNotes(this.currentPipeClassViewModel);
      this.commonNotesVersion++;
    } catch (e) {
      console.warn('Initial unifyAllNotes failed', e);
    }

    const authorOid = pipeClass.fromRevision?.authorOid;
    if (authorOid) {
      this.getAuthorName(authorOid);
    }

    // Load comments for all tabs
    this.loadAllComments();

    // Validate catalog freshness for all components and valves whenever a pipe class is loaded.
    // Outdated indicators are shown regardless of edit mode; the upgrade action is only enabled
    // when canEdit$ is true, so the check is not restricted to DRAFT-only.
    this.validateCatalogFreshness(pipeClass);

    // Detect review mode and reviewer membership
    this.detectReviewerMode(pipeClass);

    // Detect release context and populate release roles if present
    this.detectReleaseContext(pipeClass);
  }

  private detectReviewerMode(pipeClass: PipeClassViewModel): void {
    // RevisionStatus.REVIEW = 1 = REVIEW (DRAFT=0, REVIEW=1, APPROVAL=2, PUBLISHED=3, RETIRED=100, REJECTED=101)
    const revisionStatus = pipeClass.fromRevision?.status;
    this.isReviewMode = revisionStatus === 1;
    // Reset internal-reviewer flag on each load; the async success branches set it true when applicable.
    this.currentUserIsInternalReviewer = false;

    // DRAFT (0): must still check reviewer membership because a revision may have been
    // moved back from REVIEW and retain its assigned reviewers, who must remain read-only.
    // Use the same API-confirmed pattern as REVIEW/APPROVAL — never grant editing
    // based on URL params alone.
    if (revisionStatus === 0) {
      this.isReviewMode = false;
      this.currentUserApprovalState = null;

      if (!this.isInRevisionWorkflow) {
        this.currentUserIsReviewer = false;
        return;
      }

      const draftRevisionId = pipeClass.fromRevision?.id;
      if (!draftRevisionId) {
        this.currentUserIsReviewer = false;
        return;
      }

      this.esraPipingAPIClient.getRevisionMembers(draftRevisionId, pipeClass.id).pipe(first()).subscribe({
        next: (members) => {
          this.revisionMembers = members;
          const currentOid = this.msalService.instance.getActiveAccount()?.idTokenClaims?.['oid'] as string | undefined;
          const myRole = members.find(
            m => m.userId === currentOid &&
                 (m.userRole === UserType._2 || m.userRole === UserType._3)
          );
          this.currentUserIsReviewer = !!myRole;
          this.currentUserIsInternalReviewer = myRole?.userRole === UserType._2;
          this.currentUserApprovalState = myRole ? myRole.approvalState : null;
          this.currentUserIsOwner = currentOid === pipeClass.fromRevision?.authorOid;

          // Only the revision owner may edit a DRAFT. Reviewers assigned while
          // the revision was in REVIEW remain read-only even after rollback to DRAFT.
          if (this.currentUserIsOwner) {
            this.grantEditAccess();
          }

          this.cdr.markForCheck();
        },
        error: () => {
          // Fail-secure on API error — keep canEdit$=false.
          this.currentUserIsReviewer = false;
          this.currentUserApprovalState = null;
          this.currentUserIsOwner = false;
        }
      });
      return;
    }

    // PUBLISHED (3), RETIRED (100), REJECTED (101): always read-only.
    if (revisionStatus === 3 || revisionStatus === 100 || revisionStatus === 101) {
      // canEditSubject is already false from initializePermissions()
      this.currentUserIsReviewer = false;
      this.currentUserApprovalState = null;
      return;
    }

    // REVIEW (1) and APPROVAL (2): must confirm the user is the owner before allowing
    // any editing. Reviewers (INTERNAL_REVIEWER=2, EXTERNAL_REVIEWER=3) must remain
    // read-only regardless of how they navigated to the page.
    // canEditSubject is already false from initializePermissions() — it stays that way
    // until the API call confirms the user is the owner.
    if (!this.isInRevisionWorkflow) {
      // Navigated directly (not from revisions dashboard) — always read-only.
      this.currentUserIsReviewer = false;
      this.currentUserApprovalState = null;
      return;
    }

    const revisionId = pipeClass.fromRevision?.id;
    if (!revisionId) { return; }

    this.esraPipingAPIClient.getRevisionMembers(revisionId, pipeClass.id).pipe(first()).subscribe({
      next: (members) => {
        this.revisionMembers = members;
        const currentOid = this.msalService.instance.getActiveAccount()?.idTokenClaims?.['oid'] as string | undefined;
        const myRole = members.find(
          m => m.userId === currentOid &&
               (m.userRole === UserType._2 || m.userRole === UserType._3)
        );
        this.currentUserIsReviewer = !!myRole;
        this.currentUserIsInternalReviewer = myRole?.userRole === UserType._2;
        this.currentUserApprovalState = myRole ? myRole.approvalState : null;
        this.currentUserIsOwner = currentOid === pipeClass.fromRevision?.authorOid;

        // Restore field editing ONLY for the revision owner (author).
        // Pure reviewers (userRole=2 INTERNAL_REVIEWER, userRole=3 EXTERNAL_REVIEWER)
        // must remain read-only even when canEdit was true before this call.
        // If the owner is also listed as a reviewer, ownership takes precedence.
        if (this.currentUserIsOwner) {
          this.grantEditAccess();
        }

        this.cdr.markForCheck();
      },
      error: () => {
        // Fail-secure: keep canEdit$=false on API error.
        this.currentUserIsReviewer = false;
        this.currentUserApprovalState = null;
        this.currentUserIsOwner = false;
      }
    });
  }

  /**
   * Detects if the pipe class is part of a release and populates release context state.
   * When a pipe class is in release context, viewer/reviewer status is determined by
   * roles in `releaseObjectGroup.roles` rather than revision members.
   */
  private detectReleaseContext(pipeClass: PipeClassViewModel): void {
    if (!pipeClass.releaseObjectGroup) {
      this.isInReleaseContext = false;
      this.releaseRoles = [];
      this.releaseCurrentUserApprovalState = null;
      this.releaseCurrentUserIsReviewer = false;
      this.releaseCurrentUserIsInternalReviewer = false;
      return;
    }

    this.isInReleaseContext = true;
    this.releaseRoles = pipeClass.releaseObjectGroup.roles ?? [];

    // Determine current user's approval state in the release context
    const currentOid = this.msalService.instance.getActiveAccount()?.idTokenClaims?.['oid'] as string | undefined;
    const userRole = this.releaseRoles.find(
      r => r.userId === currentOid &&
           (r.userRole === UserType._2 || r.userRole === UserType._3)
    );

    this.releaseCurrentUserIsReviewer = !!userRole;
    this.releaseCurrentUserIsInternalReviewer = userRole?.userRole === UserType._2;
    this.releaseCurrentUserApprovalState = userRole ? userRole.approvalState : null;

    this.cdr.markForCheck();
  }

  /**
   * Updates `releaseRoles` and `releaseCurrentUserApprovalState` from the `RoleViewModel[]`
   * returned by `updateReviewerApproval` when in release context. The backend returns the same
   * shape for both revision and release calls; we apply the new `approvalState` values to the
   * matching entries in `releaseRoles` (matched by userId + userRole) and re-derive the current
   * user's cached approval state — mirroring the logic in `detectReleaseContext`.
   */
  private applyReleaseApprovalResponse(roles: RoleViewModel[]): void {
    // Apply updated approval states to the cached release role entries.
    // A user may appear as both INTERNAL_REVIEWER and EXTERNAL_REVIEWER; update both rows.
    for (const role of roles) {
      const idx = this.releaseRoles.findIndex(
        r => r.userId === role.userId && r.userRole === role.userRole
      );
      if (idx >= 0) {
        // Mutate in-place to preserve the NSwag-generated class instance (init/toJSON methods).
        this.releaseRoles[idx].approvalState = role.approvalState;
      }
    }

    // Re-derive current user's approval state from updated roles (same logic as detectReleaseContext).
    const currentOid = this.msalService.instance.getActiveAccount()?.idTokenClaims?.['oid'] as string | undefined;
    const userRole = this.releaseRoles.find(
      r => r.userId === currentOid &&
           (r.userRole === UserType._2 || r.userRole === UserType._3)
    );
    this.releaseCurrentUserApprovalState = userRole ? (userRole.approvalState ?? null) : null;
  }

  exportToExcel(guid: string) {
    return this.downloadService.downloadFile(
      this.esraPipingAPIClient
        .exportPipeClassToExcel(guid)
        .pipe(indicate(this.exportIndicator$))
    );
  }

  exportToPdf(guid: string) {
    return this.downloadService.downloadFile(
      this.esraPipingAPIClient
        .exportPipeClassToPdf(guid)
        .pipe(indicate(this.exportIndicator$))
    );
  }

  /**
   * Builds the fetch function passed to `app-share-link-button` for a given pipe class.
   * The backend returns a relative URL without a leading slash (e.g. "p/CES/ON/12DF01");
   * the frontend origin is prepended here since the shared button component only deals in
   * absolute, ready-to-copy URLs.
   * @param pipeClassId The pipe class id to build the share link for.
   * @returns A function returning an Observable of the absolute share URL.
   */
  getPipeClassShareUrlFn = (pipeClassId: string): () => Observable<string> =>
    () => this.esraPipingAPIClient.pipeClassShareLink(pipeClassId).pipe(
      map((response: ShareLinkResponse) => `${window.location.origin}/${(response.relativeUrl ?? '').replace(/^\/+/, '')}`)
    );

  getUserEmail(authorOid: string) {
    this.graphService$ = this.graphService.getUserByOid(authorOid);
    this.graphService$.subscribe((user) => {
      this.userEmail.next(user.mail);
    });
  }

  getAuthorName(authorOid: string) {
    if (!authorOid) {
      this.authorName.next('Unknown Author');
      return;
    }

    this.graphService.getUserByOid(authorOid).subscribe({
      next: (user) => {
        this.authorName.next(user?.displayName || 'Unknown Author');
      },
      error: () => {
        this.authorName.next('Unknown Author');
      }
    });
  }

  /**
   * Resolves the display name for the author of the previous revision and stores it in
   * `previousAuthorName$`. Called after `previousPipeClassViewModel` is set. The author OID
   * lives on `fromRevision.authorOid`, not directly on the pipe class view model.
   */
  private getPreviousAuthorName(): void {
    const authorOid: string | undefined = this.previousPipeClassViewModel?.fromRevision?.authorOid;
    if (!authorOid) {
      this.previousAuthorName.next('');
      return;
    }
    this.graphService.getUserByOid(authorOid).subscribe({
      next: (user) => {
        this.previousAuthorName.next(user?.displayName || '');
      },
      error: () => {
        this.previousAuthorName.next('');
      }
    });
  }

  public onTabChange(event) {
    // Tab change logic can be added here if needed
  }

  saveDraft(): void {
    if (this.blockSaveIfLifecycleOutdated()) { return; }

    if (this.requiresReviseOnSave) {
      // The lifecycle warning replaces the generic confirmation so the user answers once.
      this.releaseLifecycleService.confirmReviseOnSave().pipe(first()).subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.performSaveDraft(true);
        }
      });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        dialogMessage: 'Do you wish to save this specification?',
        confirmText: 'save',
        cancelText: 'cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // User clicked "save draft" - implement save logic here
        this.performSaveDraft();
      }
    });
  }

  /**
   * Returns `true` when at least one PTT or branch table is confirmed out of sync.
   * Disables "Confirm Info to Review" until the user syncs all stale tables.
   */
  get hasPendingSyncs(): boolean {
    const pttUnsynced: boolean = this.pipingSummaryComponent?.hasUnsyncedPTTTables() ?? false;
    const branchUnsynced: boolean = this.branchTablesComponent?.hasUnsyncedBranchTables() ?? false;
    return pttUnsynced || branchUnsynced;
  }

  confirmForReview(): void {
    if (this.blockSaveIfLifecycleOutdated()) { return; }
    if (this.warnIfIncompleteFields()) { return; }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        dialogMessage: 'Do you wish to confirm this specification for review?',
        confirmText: 'yes',
        cancelText: 'cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.performSaveThenConfirmForReview();
      }
    });
  }

  returnToDraft(): void {
    this.performReturnToDraft();
  }

  performReturnToDraft(): void {
    const revisionId = this.currentPipeClassViewModel?.fromRevision?.id;
    const revision = this.currentPipeClassViewModel?.fromRevision;
    if (!revisionId || !revision) { return; }

    const members = this.generalDetailsComponent ? this.generalDetailsComponent.getCurrentMembers() : [];

    // Save members first, then change status — changeRevisionStatus no longer accepts a body.
    this.esraPipingAPIClient.updateRevisionMembers(revisionId, members).pipe(
      switchMap(() => this.esraPipingAPIClient.changeRevisionStatus(revisionId, RevisionStatus.DRAFT, this.isInReleaseContext ? this.pipeClassId : undefined, revision.description))
    ).subscribe({
      next: () => { this.announceReviewChange(() => window.location.reload()); },
      error: (error: any) => {
        console.error('Request failed:', error);
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Statuses from which a revision is allowed to transition to PUBLISHED.
   * Matches backend PublishableStatuses array in RevisionService.
   */
  private static readonly PUBLISHABLE_STATUSES: RevisionStatus[] = [
    RevisionStatus.APPROVAL,   // APPROVAL
    RevisionStatus.REVIEW,   // REVIEW
    RevisionStatus.REVIEW_WITH_REJECTIONS,  // REVIEW_WITH_REJECTIONS
    RevisionStatus.REVIEW_WITH_PENDING_COMMENTS,  // REVIEW_WITH_PENDING_COMMENTS
    RevisionStatus.APPROVAL_WITH_PENDING_COMMENTS,  // APPROVAL_WITH_PENDING_COMMENTS
  ];

  /**
   * The status that governs button visibility and publish eligibility.
   * In release context, uses `releaseObjectGroup.reviewStatus` — the server-computed
   * status for this object within the release — rather than the underlying revision
   * status, which may differ (e.g. revision is REVIEW but the release slot is Draft).
   */
  get effectiveRevisionStatus(): RevisionStatus | undefined {
    if (this.isInReleaseContext) {
      return this.currentPipeClassViewModel?.releaseObjectGroup?.reviewStatus;
    }
    return this.currentPipeClassViewModel?.fromRevision?.status;
  }

  /**
   * Returns true if the current revision can be published based on:
   * 1. Current status is in the publishable statuses list
   * 2. Current user is either the author or an approver (UserType._1)
   */
  get canPublishRevision(): boolean {
    const status = this.effectiveRevisionStatus;
    if (status === undefined || status === null) { return false; }

    // Check if revision status is publishable
    const isPublishableStatus = PipingTabsParentComponent.PUBLISHABLE_STATUSES.includes(status);
    if (!isPublishableStatus) { return false; }

    // Check if user is author or approver
    const currentOid = this.msalService.instance.getActiveAccount()?.idTokenClaims?.['oid'] as string | undefined;
    const isAuthor = currentOid === this.currentPipeClassViewModel?.fromRevision?.authorOid;
    const isApprover = this.revisionMembers.some(m => m.userId === currentOid && m.userRole === UserType._1);

    return isAuthor || isApprover;
  }

  /** Indicator for publish operation in progress. */
  publishingIndicator$ = new BehaviorSubject<boolean>(false);

  /**
   * Opens confirmation dialog and publishes the revision if confirmed.
   */
  publishRevision(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        dialogMessage: 'Are you sure you want to publish this revision? This action will make the revision publicly available.',
        confirmText: 'Publish',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.performPublishRevision();
      }
    });
  }

  /**
   * Calls the API to publish the current revision.
   * Surfaces the server error message on 400 (e.g. "at least one Revision Owner must be assigned").
   */
  private performPublishRevision(): void {
    const revisionId = this.currentPipeClassViewModel?.fromRevision?.id;
    if (!revisionId) { return; }

    this.esraPipingAPIClient.publishRevision(revisionId)
      .pipe(indicate(this.publishingIndicator$), first())
      .subscribe({
        next: () => {
          // Reload page to reflect new published status
          window.location.reload();
        },
        error: (error: any) => {
          console.error('Failed to publish revision:', error);
          this.dialog.open(CustomAlertDialogComponent, {
            width: '450px',
            data: {
              message: this.extractApiErrorMessage(error, 'Failed to publish the revision. Please try again.'),
              buttonText: 'okay'
            }
          });
          this.cdr.markForCheck();
        }
      });
  }

  private performConfirmForReview(statustoupdate: string): void {
    const revisionId = this.currentPipeClassViewModel?.fromRevision?.id;
    const revision = this.currentPipeClassViewModel?.fromRevision;
    if (!revisionId || !revision) { return; }

    const members = this.generalDetailsComponent ? this.generalDetailsComponent.getCurrentMembers() : [];

    // Save members first, then change status — changeRevisionStatus no longer accepts a body.
    this.esraPipingAPIClient.updateRevisionMembers(revisionId, members).pipe(
      switchMap(updatedRevision => {
        if (updatedRevision?.roles?.length && this.generalDetailsComponent) {
          this.generalDetailsComponent.updateRolesFromResponse(updatedRevision.roles);
        }
        return this.esraPipingAPIClient.changeRevisionStatus(revisionId, RevisionStatus.REVIEW, this.isInReleaseContext ? this.pipeClassId : undefined, revision.description);
      })
    ).subscribe({
      next: () => { this.cdr.detectChanges(); },
      error: (error: any) => {
        console.error('Request failed:', error);
        this.dialog.open(CustomAlertDialogComponent, {
          width: '450px',
          data: {
            message: this.extractApiErrorMessage(error, 'Failed to submit the revision for review. Please try again.'),
            buttonText: 'okay'
          }
        });
        this.cdr.markForCheck();
      }
    });
  }
  /**
   * Extracts a user-displayable message from an API error.
   * NSwag throws the raw response body string directly for 400 responses;
   * for other error types it falls back to `error.message` or the provided fallback.
   */
  private extractApiErrorMessage(error: any, fallback: string): string {
    if (typeof error === 'string' && error.trim().length > 0) {
      // The server may return a JSON-encoded string (e.g. '"some message"');
      // unwrap the outer quotes if present.
      const trimmed: string = error.trim();
      if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        try { return JSON.parse(trimmed); } catch { /* not valid JSON — return as-is */ }
      }
      return trimmed;
    }
    if (error?.message && typeof error.message === 'string') {
      return error.message;
    }
    return fallback;
  }

  /**
   * Fetches full branch table data (including `cells`) for any branch tables that were
   * returned by the pipe class endpoint without cells.
   *
   * The `getPipeClassByPipeClassId` (and `updatePipeClass`) endpoints return branch tables
   * as lightweight references — `cells` is omitted to keep the payload small. Without cells
   * the `*ngIf="branch.cells?.length > 0"` guard in the template hides the entire table
   * graphic. This method uses `getBranchTableViewModelById` (the full dictionary entry) to
   * retrieve cells for each table and patches them into the in-memory list.
   */
  private enrichBranchTableCells(): void {
    if (!this.branchTableViewModelList?.length) { return; }

    // Only fetch tables that are actually missing cells — avoids redundant round-trips.
    const tablesNeedingCells: BranchTableViewModel[] = this.branchTableViewModelList.filter(
      bt => !bt.cells?.length && !!bt.branchTableId
    );
    if (!tablesNeedingCells.length) { return; }

    // Fetch each missing table from the dictionary in parallel.
    // getBranchTableViewModelById returns the full dictionary entry including cells,
    // whereas getBranchTableViewModelByPipeClassId returns the same lightweight data
    // that the pipe class endpoint uses (no cells).
    const cellRequests: Observable<BranchTableViewModel | null>[] = tablesNeedingCells.map(bt =>
      this.esraPipingAPIClient
        .getBranchTableViewModelById(bt.branchTableId)
        .pipe(catchError(() => of(null)))
    );

    forkJoin(cellRequests).pipe(first()).subscribe((fullTables: (BranchTableViewModel | null)[]) => {
      let changed: boolean = false;
      fullTables.forEach((fullTable: BranchTableViewModel | null, i: number) => {
        if (fullTable?.cells?.length) {
          const bt: BranchTableViewModel = tablesNeedingCells[i];
          bt.cells = fullTable.cells;
          // Preserve codes already on the relation; fall back to dictionary when absent.
          if (!bt.codes?.length) { bt.codes = fullTable.codes; }
          changed = true;
        }
      });

      if (changed) {
        // Force array reference change so child components see the update via ngOnChanges.
        this.branchTableViewModelList = [...this.branchTableViewModelList];
        if (this.currentPipeClassViewModel) {
          this.currentPipeClassViewModel.branchTables = this.branchTableViewModelList;
        }
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Reviewer: approve the current revision.
   */
  approveRevision(): void {
    const revisionId = this.currentPipeClassViewModel?.fromRevision?.id;
    if (!revisionId) return;

    // Pass objectId only in release context — the backend uses it to identify
    // the correct release object group instead of the revision membership.
    const objectId: string | undefined = this.isInReleaseContext ? this.currentPipeClassViewModel?.id : undefined;
    // Internal reviewers record NoComments (_3); external reviewers/approvers record Approved (_1).
    const approvalState: ApprovalState = this.activeUserIsInternalReviewer ? ApprovalState._3 : ApprovalState._1;
    this.esraPipingAPIClient.updateReviewerApproval(revisionId, objectId, approvalState)
      .pipe(indicate(this.approvingIndicator$), first())
      .subscribe({
        next: (roles) => {
          if (this.isInReleaseContext) {
            this.applyReleaseApprovalResponse(roles);
          } else {
            if (roles.length > 0) { this.currentUserApprovalState = roles[0].approvalState; }
            for (const role of roles) {
              const idx = this.revisionMembers.findIndex(m => m.userId === role.userId && m.userRole === role.userRole);
              if (idx >= 0) {
                // Mutate in-place to preserve the NSwag-generated class instance.
                this.revisionMembers[idx].approvalState = role.approvalState;
              }
            }
          }
          this.cdr.markForCheck();
          this.announceReviewChange();
        },
        error: (err) => console.error('Error approving revision', err)
      });
  }

  /**
   * Reviewer: reject the current revision.
   */
  rejectRevision(): void {
    const revisionId = this.currentPipeClassViewModel?.fromRevision?.id;
    if (!revisionId) return;

    const objectId: string | undefined = this.isInReleaseContext ? this.currentPipeClassViewModel?.id : undefined;
    // Internal reviewers record Commented (_4); external reviewers/approvers record Rejected (_2).
    const approvalState: ApprovalState = this.activeUserIsInternalReviewer ? ApprovalState._4 : ApprovalState._2;
    this.esraPipingAPIClient.updateReviewerApproval(revisionId, objectId, approvalState)
      .pipe(indicate(this.approvingIndicator$), first())
      .subscribe({
        next: (roles) => {
          if (this.isInReleaseContext) {
            this.applyReleaseApprovalResponse(roles);
          } else {
            if (roles.length > 0) { this.currentUserApprovalState = roles[0].approvalState; }
            for (const role of roles) {
              const idx = this.revisionMembers.findIndex(m => m.userId === role.userId && m.userRole === role.userRole);
              if (idx >= 0) {
                // Mutate in-place to preserve the NSwag-generated class instance.
                this.revisionMembers[idx].approvalState = role.approvalState;
              }
            }
          }
          this.cdr.markForCheck();
          this.announceReviewChange();
        },
        error: (err) => console.error('Error rejecting revision', err)
      });
  }

  /**
   * Reviewer: reset approval state back to Pending.
   */
  resetApproval(): void {
    const revisionId = this.currentPipeClassViewModel?.fromRevision?.id;
    if (!revisionId) return;

    const objectId: string | undefined = this.isInReleaseContext ? this.currentPipeClassViewModel?.id : undefined;
    this.esraPipingAPIClient.updateReviewerApproval(revisionId, objectId, ApprovalState._0)
      .pipe(indicate(this.approvingIndicator$), first())
      .subscribe({
        next: (roles) => {
          if (this.isInReleaseContext) {
            this.applyReleaseApprovalResponse(roles);
          } else {
            if (roles.length > 0) { this.currentUserApprovalState = roles[0].approvalState; }
            for (const role of roles) {
              const idx = this.revisionMembers.findIndex(m => m.userId === role.userId && m.userRole === role.userRole);
              if (idx >= 0) {
                // Mutate in-place to preserve the NSwag-generated class instance.
                this.revisionMembers[idx].approvalState = role.approvalState;
              }
            }
          }
          this.cdr.markForCheck();
          this.announceReviewChange();
        },
        error: (err) => console.error('Error resetting approval', err)
      });
  }

  /**
   * Toggle compare mode on/off. When entering compare mode for the first time,
   * fetches the previous pipe class version for side-by-side comparison.
   */
  toggleCompareMode(): void {
    if (this.isCompareMode) {
      // Exiting compare mode
      this.isCompareMode = false;
      this.cdr.markForCheck();
      return;
    }

    // Entering compare mode — fetch previous pipe class if not already loaded
    const previousId: string | undefined = this.currentPipeClassViewModel?.previousPipeClass;
    if (!previousId) {
      // No previous version available
      this.isCompareMode = true;
      this.cdr.markForCheck();
      return;
    }

    if (this.previousPipeClassViewModel) {
      // Already fetched — just toggle; author name was resolved on first fetch
      this.isCompareMode = true;
      this.cdr.markForCheck();
      return;
    }

    this.esraPipingAPIClient.getPipeClassByPipeClassId(previousId)
      .pipe(indicate(this.compareLoadingIndicator$), first())
      .subscribe({
        next: (previousPipeClass: PipeClassViewModel) => {
          // Apply the same override normalization used for the current pipe class so that
          // valve rating/ends reflect the per-pipe-class overrides, not the raw catalog values.
          this.normalizeValveOverrides(previousPipeClass);
          this.previousPipeClassViewModel = previousPipeClass;
          // Resolve the previous revision's author name for the header compare display
          this.getPreviousAuthorName();
          this.isCompareMode = true;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error fetching previous pipe class for comparison', err);
          // Still enter compare mode, just without previous data
          this.isCompareMode = true;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Whether the compare button should be visible.
   * Shown when revision status is not DRAFT (0), PUBLISHED (3), RETIRED (100), or REJECTED (101).
   */
  get canShowCompareButton(): boolean {
    const status: number | undefined = this.currentPipeClassViewModel?.fromRevision?.status;
    if (status === undefined || status === null) return false;
    // Show for REVIEW (1), APPROVAL (2), and pseudo-statuses (10, 11, 12)
    return status !== 0 && status !== 3 && status !== 100 && status !== 101;
  }

  /**
   * Validates that all required Summary tab fields are filled in.
   * @returns An array of human-readable warning messages for each missing field. Empty when all fields are complete.
   */
  private validateSummaryFields(): string[] {
    const issues: string[] = [];
    const vm: PipeClassViewModel | null = this.currentPipeClassViewModel;
    if (!vm) { return issues; }

    const checkField = (value: string | null | undefined, label: string): void => {
      if (!value?.trim()) { issues.push(`Summary tab: "${label}" is required.`); }
    };

    checkField(vm.service?.name, 'Service');
    checkField((vm.ratingClass as any)?.value, 'Rating Class');

    // Temperature Limit: both minimum and maximum must be present, and max must be >= min
    const tempRange = vm.temperatureRange as any;
    const hasMinTemp: boolean = tempRange?.minimum !== null && tempRange?.minimum !== undefined && String(tempRange.minimum).trim() !== '';
    const hasMaxTemp: boolean = tempRange?.maximum !== null && tempRange?.maximum !== undefined && String(tempRange.maximum).trim() !== '';
    if (!hasMinTemp || !hasMaxTemp) {
      issues.push('Summary tab: "Temperature Limit" is required.');
    } else if (Number(tempRange.maximum) < Number(tempRange.minimum)) {
      issues.push('Summary tab: "Temperature Limit" Max Temperature cannot be less than Min Temperature.');
    }

    checkField((vm.nominalCorrosionAllowance as any)?.value, 'Nominal Corrosion Allowance');
    checkField((vm.valveTrim as any)?.value, 'Valve Trim');
    checkField((vm.material as any)?.value, 'Material');
    checkField((vm.designCode as any)?.value, 'Design Code');
    checkField((vm.stressRelief as any)?.value, 'Stress Relief');
    checkField((vm.examination as any)?.value, 'Examination');
    checkField((vm.limitedBy as any)?.value, 'Limited by');

    // General Details: at least one Revision Owner and one Internal Reviewer must be assigned
    const ownerCount: number = this.generalDetailsComponent?.ownerRoles?.length ?? 0;
    const reviewerCount: number = this.generalDetailsComponent?.reviewerRoles?.length ?? 0;
    if (ownerCount === 0) { issues.push('General Details tab: at least one Revision Owner must be assigned.'); }
    if (reviewerCount === 0) { issues.push('General Details tab: at least one Internal Reviewer must be assigned.'); }

    return issues;
  }

  /**
   * Validates that all components have NPS set, and all valves have NPS, Rating, and Ends set.
   * @returns An array of human-readable warning messages, one per incomplete item. Empty when all items are complete.
   */
  private validateComponentsAndValvesCompleteness(): string[] {
    const issues: string[] = [];

    const components: ComponentsInPipeClassViewModel[] = this.currentPipeClassViewModel?.componentsInPipeClass ?? [];
    components.forEach((item: ComponentsInPipeClassViewModel, index: number) => {
      if (!item.component?.nps?.trim()) {
        const label: string = item.component?.type || item.component?.description || `Component #${index + 1}`;
        issues.push(`Components tab: "${label}" is missing NPS.`);
      }
    });

    const valves: ValveViewModel[] = this.currentPipeClassViewModel?.valves ?? [];
    valves.forEach((valve: ValveViewModel, index: number) => {
      const label: string = valve.tag || `Valve #${index + 1}`;
      const missingFields: string[] = [];
      if (!valve.nps?.trim()) { missingFields.push('NPS'); }
      if (!valve.rating?.trim()) { missingFields.push('Rating'); }
      if (!valve.ends?.trim()) { missingFields.push('Ends'); }
      if (missingFields.length > 0) {
        issues.push(`Valves tab: "${label}" is missing ${missingFields.join(', ')}.`);
      }
    });

    return issues;
  }

  /**
   * Aggregates validation issues from all tabs (Summary, Components, Valves).
   * Shows a warning dialog when any issues are found and returns true to allow callers to abort.
   * Returns false when everything is complete and the save/status-change may proceed.
   */
  private warnIfIncompleteFields(): boolean {
    const issues: string[] = [
      ...this.validateSummaryFields(),
      ...this.validateComponentsAndValvesCompleteness()
    ];
    if (issues.length === 0) { return false; }

    const message: string =
      'Please complete the following required fields before saving:\n\n' +
      issues.map((issue: string) => `\u2022 ${issue}`).join('\n');

    this.dialog.open(CustomAlertDialogComponent, {
      width: '500px',
      data: {
        title: 'incomplete items',
        message,
        buttonText: 'okay'
      }
    });
    return true;
  }

  /**
   * Perform the actual save draft operation
   * @param lifecycleConfirmed True when the user already accepted the "release as is" to "revise" move.
   */
  performSaveDraft(lifecycleConfirmed: boolean = false): void {
    if (!this.currentPipeClassViewModel) {
      return;
    }

    if (this.blockSaveIfLifecycleOutdated()) { return; }

    if (this.warnIfIncompleteFields()) { return; }

    if (!lifecycleConfirmed && this.requiresReviseOnSave) {
      this.releaseLifecycleService.confirmReviseOnSave().pipe(first()).subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.performSaveDraft(true);
        }
      });
      return;
    }

    try {
      // Collect all modified data from child components
      const pipeClassToSave = this.collectModifiedData();

      // Debug: Log what we're sending to backend
      console.log('[performSaveDraft] Sending to backend - Current revision:', pipeClassToSave.fromRevision?.name);
      if (pipeClassToSave.valves && pipeClassToSave.valves.length > 0) {
        console.log('[performSaveDraft] Valves before save (sending to backend):', pipeClassToSave.valves.map(v => ({
          tag: v.tag,
          fromRevisionName: v.fromRevision?.name,
          fromRevisionId: v.fromRevision?.id,
          __isModified: (v as any).__isModified
        })));
      }

      this.esraPipingAPIClient.updatePipeClass(pipeClassToSave)
        .pipe(
          indicate(this.savingIndicator$),
          first()
        )
        .subscribe({
          next: (updatedPipeClass: PipeClassViewModel) => {

            // Debug: Log what backend returned for valves
            console.log('[performSaveDraft] Backend response - Current revision:', updatedPipeClass.fromRevision?.name);
            if (updatedPipeClass.valves && updatedPipeClass.valves.length > 0) {
              console.log('[performSaveDraft] Valves after save:', updatedPipeClass.valves.map(v => ({
                tag: v.tag,
                fromRevisionName: v.fromRevision?.name,
                __isModified: (v as any).__isModified
              })));
            }
            if (updatedPipeClass.componentsInPipeClass && updatedPipeClass.componentsInPipeClass.length > 0) {
              console.log('[performSaveDraft] Components after save:', updatedPipeClass.componentsInPipeClass.map(c => ({
                type: c.component?.type,
                lastUpdatedRevision: c.lastUpdatedRevision,
                __isModified: (c as any).__isModified
              })));
            }

            // Update local data with server response
            this.announceLifecycleChange(this.currentPipeClassViewModel?.releaseObjectGroup, updatedPipeClass.releaseObjectGroup);
            this.currentPipeClassViewModel = updatedPipeClass;
            this.branchTableViewModelList = updatedPipeClass.branchTables;
            // Re-enrich cells after save — the save response omits them for the same
            // reason as the load response (payload size), so they must be patched back in.
            this.enrichBranchTableCells();
            this.ensureValveDataConsistency();
            this.updateObservableDataSource();
            // Clear pending changes since save was successful
            this.clearPendingChanges();

            // Also persist revision members (roles) via updateRevisionMembers.
            // updatePipeClass only saves pipe class data — members must be saved separately.
            // changeRevisionStatus is only for status transitions (DRAFT→REVIEW etc.), not for Save Draft.
            const revision = updatedPipeClass.fromRevision;
            if (revision?.id) {
              const members = this.generalDetailsComponent ? this.generalDetailsComponent.getCurrentMembers() : [];
              this.esraPipingAPIClient.updateRevisionMembers(revision.id, members).subscribe({
                next: (updatedRevision: PipingRevisionViewModel) => {
                  if (updatedRevision?.roles?.length && this.generalDetailsComponent) {
                    this.generalDetailsComponent.updateRolesFromResponse(updatedRevision.roles);
                  }
                },
                error: (memberError: any) => {
                  console.error('Error saving revision members during draft save:', memberError);
                }
              });
            }

            // Show success message
            this.dialog.open(CustomAlertDialogComponent, {
              width: '400px',
              data: {
                message: 'saved successfully!',
                buttonText: 'okay'
              }
            });

            // Force change detection to update UI
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Error saving pipe class:', error);

            // Enhanced error handling with detailed message
            let errorMessage = 'Error saving draft. Please try again.';

            if (error.status === 400 && error.errors) {
              console.error('Validation errors:', error.errors);
              errorMessage = 'Validation errors occurred:\n';

              // Extract first few validation errors for display
              const errorKeys = Object.keys(error.errors);
              const firstErrors = errorKeys.slice(0, 3);

              firstErrors.forEach(key => {
                const messages = error.errors[key];
                if (Array.isArray(messages) && messages.length > 0) {
                  errorMessage += `• ${key}: ${messages[0]}\n`;
                }
              });

              if (errorKeys.length > 3) {
                errorMessage += `... and ${errorKeys.length - 3} more validation errors.`;
              }

              errorMessage += '\n\nPlease check the console for full details.';
            }

            this.dialog.open(CustomAlertDialogComponent, {
              width: '400px',
              data: {
                message: errorMessage,
                buttonText: 'okay'
              }
            });
          }
        });
    } catch (error) {
      console.error('Error collecting modified data:', error);
      this.dialog.open(CustomAlertDialogComponent, {
        width: '400px',
        data: {
          message: 'Error preparing data for save. Please try again.',
          buttonText: 'okay'
        }
      });
    }
  }

  /**
   * Save draft first, then confirm for review if save succeeds.
   */
  private performSaveThenConfirmForReview(lifecycleConfirmed: boolean = false): void {
    if (!this.currentPipeClassViewModel) {
      return;
    }

    if (this.blockSaveIfLifecycleOutdated()) { return; }

    if (!lifecycleConfirmed && this.requiresReviseOnSave) {
      this.releaseLifecycleService.confirmReviseOnSave().pipe(first()).subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.performSaveThenConfirmForReview(true);
        }
      });
      return;
    }

    let pipeClassToSave: PipeClassViewModel;
    try {
      pipeClassToSave = this.collectModifiedData();
    } catch (error) {
      console.error('Error collecting modified data:', error);
      this.dialog.open(CustomAlertDialogComponent, {
        width: '400px',
        data: {
          message: 'Error preparing data for save. Review confirmation was cancelled.',
          buttonText: 'okay'
        }
      });
      return;
    }

    // Manually manage the spinner so it stays on across both API calls.
    // `indicate` relies on `finalize`, which fires when `first()` unsubscribes from the
    // save observable — before `switchMap` starts the second call. By setting the indicator
    // explicitly here and clearing it only in `finalize` at the very end of the full chain,
    // the spinner remains visible for the entire save → confirm-for-review sequence.
    this.savingIndicator$.next(true);
    this.cdr.markForCheck();

    this.esraPipingAPIClient.updatePipeClass(pipeClassToSave)
      .pipe(
        first(),
        // Intercept save errors here so the outer error handler knows this was a save failure
        catchError(saveError => {
          console.error('Error saving pipe class during confirm-for-review:', saveError);
          this.dialog.open(CustomAlertDialogComponent, {
            width: '400px',
            data: { message: 'Error saving draft. Review confirmation was cancelled.', buttonText: 'okay' }
          });
          return throwError(() => saveError);
        }),
        tap((updatedPipeClass: PipeClassViewModel) => {
          // Update local state as soon as the save responds, before the status call
          this.announceLifecycleChange(this.currentPipeClassViewModel?.releaseObjectGroup, updatedPipeClass.releaseObjectGroup);
          this.currentPipeClassViewModel = updatedPipeClass;
          this.branchTableViewModelList = updatedPipeClass.branchTables;
          // Re-enrich cells after save — the save response omits them for the same
          // reason as the load response (payload size), so they must be patched back in.
          this.enrichBranchTableCells();
          this.ensureValveDataConsistency();
          this.updateObservableDataSource();
          this.clearPendingChanges();
        }),
        switchMap((updatedPipeClass: PipeClassViewModel) => {
          const revision = updatedPipeClass.fromRevision;

          if (!revision || !revision.id) {
            throw new Error('Could not determine the revision to confirm.');
          }

          // changeRevisionStatus no longer accepts a body — save members first, then change status.
          const members = this.generalDetailsComponent ? this.generalDetailsComponent.getCurrentMembers() : [];

          return this.esraPipingAPIClient.updateRevisionMembers(revision.id, members).pipe(
            catchError(statusError => {
              console.error('Error saving members during confirm-for-review:', statusError);
              this.dialog.open(CustomAlertDialogComponent, {
                width: '400px',
                data: { message: 'Error confirming revision for review. Please try again.', buttonText: 'okay' }
              });
              return throwError(() => statusError);
            }),
            switchMap((updatedRevision: PipingRevisionViewModel) => {
              if (updatedRevision?.roles?.length && this.generalDetailsComponent) {
                this.generalDetailsComponent.updateRolesFromResponse(updatedRevision.roles);
              }
              return this.esraPipingAPIClient.changeRevisionStatus(revision.id, RevisionStatus.REVIEW, this.isInReleaseContext ? this.pipeClassId : undefined, revision.description);
            }),
            catchError(statusError => {
              console.error('Error confirming revision for review:', statusError);
              this.dialog.open(CustomAlertDialogComponent, {
                width: '450px',
                data: {
                  message: this.extractApiErrorMessage(statusError, 'Failed to submit the revision for review. Please try again.'),
                  buttonText: 'okay'
                }
              });
              return throwError(() => statusError);
            }),
            switchMap(() => of(updatedPipeClass))
          );
        }),
        // Turn the spinner off once the full chain completes or errors
        finalize(() => {
          this.savingIndicator$.next(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (updatedPipeClass: PipeClassViewModel) => {
          const specName: string = updatedPipeClass.name || this.pipeClassName || 'this';
          this.announceReviewChange();
          const confirmDialogRef = this.dialog.open(CustomAlertDialogComponent, {
            width: '500px',
            data: {
              title: 'revision confirmed',
              message: `Thank you for completing the revision for ${specName} specification. \nAn email will be sent to the reviewer(s) to begin the review process.`,
              buttonText: 'done'
            }
          });

          confirmDialogRef.afterClosed().subscribe(() => {
            this.router.navigate(['/piping/revisionsAndReleases']);
          });
        },
        // Both catchError operators above already show a dialog and re-throw,
        // so this handler only needs to swallow the re-thrown error silently.
        error: (error) => {
          console.error('Error during save-then-confirm-for-review:', error);
        }
      });
  }

  /**
   * Submit the confirm-for-review request to the API.
   * On success, shows a confirmation dialog and navigates to revisions & releases.
   * @deprecated The full flow is now handled by `performSaveThenConfirmForReview`.
   */
  private submitConfirmForReview(pipeClass: PipeClassViewModel): void {
    // Kept as a no-op stub to avoid breaking any lingering call sites.
    // All logic has been moved into performSaveThenConfirmForReview.
  }

  /**
   * Collect all modified data from child components and merge it into the pipe class
   * This method will be expanded as we implement data collection from each child component
   */
  private collectModifiedData(): PipeClassViewModel {
    if (!this.currentPipeClassViewModel) {
      throw new Error('No base pipe class data available');
    }

    // Use the original object directly to maintain proper typing
    const modifiedPipeClass = this.currentPipeClassViewModel;
    if (this.branchTableViewModelList !== undefined) {
      modifiedPipeClass.branchTables = this.branchTableViewModelList;
    }
    this.ensureValveDataConsistency();

    // Ensure each component maintains its independent notes during save
    this.ensureComponentNotesIndependence(modifiedPipeClass);

    // Derive npsFrom/npsTo for any component where they are null but nps is set.
    // This covers legacy data loaded from the GET before the BE migration populated the boundaries.
    this.normalizeComponentNpsBoundaries(modifiedPipeClass);

    // Clean data to avoid validation errors
    this.cleanPipeClassData(modifiedPipeClass);

    // Apply PTT hydrotest notes to RelationTestNotes before saving
    this.applyPTTHydrotestNotes(modifiedPipeClass);

    // Apply PTT additional notes to RelationAdditionalNotes before saving
    this.applyPTTAdditionalNotes(modifiedPipeClass);

    // Apply PTT common notes to RelationCommonNotes before saving
    this.applyPTTCommonNotes(modifiedPipeClass);

    // Apply pendingChanges deltas for components & valves notes if any
    this.applyPendingComponentNoteChanges(modifiedPipeClass);
    this.applyPendingValveNoteChanges(modifiedPipeClass);

    // Unify all notes (pipeClass level + component notes + valve notes) into unique set at pipeClass.commonNotes
    this.unifyAllNotes(modifiedPipeClass);

    return modifiedPipeClass;
  }

  /** Merge pending changes for component individual and group notes already applied in place but kept for delta tracking */
  private applyPendingComponentNoteChanges(pipeClass: PipeClassViewModel): void {
    // Currently component notes are directly mutated; placeholder if future diff logic needed
  }

  /**
   * Ensures npsFrom/npsTo are populated before sending to the backend.
   * Some records loaded from GET may have nps set but npsFrom/npsTo still null (pre-migration legacy data).
   * Parsing here guarantees all three fields are always in sync in the PUT payload.
   * Reducers (nps containing 'x') are skipped — their boundary UX is to be defined with the SME.
   */
  private normalizeComponentNpsBoundaries(pipeClass: PipeClassViewModel): void {
    if (!pipeClass.componentsInPipeClass) return;
    for (const item of pipeClass.componentsInPipeClass) {
      const component: ComponentViewModel = item.component;
      if (!component) continue;
      if (component.npsFrom && component.npsTo) continue; // already populated — nothing to do
      const parsed = this.npsValidationService.parseNpsRange(component.nps);
      if (parsed) {
        component.npsFrom = parsed.npsFrom;
        component.npsTo = parsed.npsTo;
      }
    }
  }

  /** Merge pending valve note changes (already applied in-place) */
  private applyPendingValveNoteChanges(pipeClass: PipeClassViewModel): void {
    // Same pattern as components; in-place updates already done
  }

  /** Build a unique union of all notes across pipeClass, components, valves, branch tables, and PTT */
  private unifyAllNotes(pipeClass: PipeClassViewModel): void {
    const noteMap = new Map<string, CommonNoteViewModel>();

    const addNotes = (notes?: CommonNoteViewModel[], source?: string) => {
      if (!notes) return;
      const addedKeys: string[] = [];
      notes.forEach(n => {
        if (!n) return;
        const key = `${n.noteId || ''}__${n.codeId || ''}`;
        if (!noteMap.has(key)) {
          addedKeys.push(`${n.codeId}-${n.noteId}`);
          // clone light to avoid shared mutation
          noteMap.set(key, new CommonNoteViewModel({
            noteId: n.noteId,
            codeId: n.codeId,
            note: n.note,
            isActive: n.isActive,
            isArchived: n.isArchived,
            isReferenced: n.isReferenced,
            updated: n.updated ? new Date(n.updated) : undefined
          }));
        }
      });

    };

    // Collect notes from Summary tab (pipeClass level)
    addNotes(pipeClass.commonNotes, 'Summary');

    // Collect notes from Components tab
    (pipeClass.componentsInPipeClass || []).forEach((c, idx) => {
      if (c.commonNotes && c.commonNotes.length > 0) {
        addNotes(c.commonNotes, `Component[${idx}]`);
      }
    });

    // Collect notes from Valves tab
    (pipeClass.valves || []).forEach((v, idx) => {
      if (v.commonNotes && v.commonNotes.length > 0) {
        addNotes(v.commonNotes, `Valve[${idx}]`);
      }
    });

    // Collect notes from Branch Tables tab
    (pipeClass.branchTables || []).forEach((bt, idx) => {
      if (bt.commonNotes && bt.commonNotes.length > 0) {
        addNotes(bt.commonNotes, `BranchTable[${idx}]`);
      }
    });

    // Collect notes from PTT (Pressure Temperature Tables) in Summary tab
    (pipeClass.pressureTemperatureTables || []).forEach((ptt, idx) => {
      if (ptt.relationCommonNotes && ptt.relationCommonNotes.length > 0) {
        addNotes(ptt.relationCommonNotes, `PTT[${idx}].relationCommonNotes`);
      }
      if (ptt.commonNotes && ptt.commonNotes.length > 0) {
        addNotes(ptt.commonNotes, `PTT[${idx}].commonNotes`);
      }
    });

    const unifiedNotes = Array.from(noteMap.values());

    unifiedNotes.sort((a, b) => {
      const codeA = (a.codeId || '').toLowerCase();
      const codeB = (b.codeId || '').toLowerCase();
      return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
    });

    pipeClass.commonNotes = unifiedNotes;
    this.commonNotesVersion++; // trigger UI refresh for common notes tab
  }

  /**
   * Apply PTT hydrotest notes from the summary component to RelationTestNotes
   */
  private applyPTTHydrotestNotes(pipeClass: PipeClassViewModel): void {
    if (!this.pipingSummaryComponent || !pipeClass.pressureTemperatureTables) {
      return;
    }

    // Iterate through each pressure temperature table
    pipeClass.pressureTemperatureTables.forEach((table, tableIndex) => {
      // Get the hydrotest note from the summary component for this table
      const hydrotestNote = this.pipingSummaryComponent.getPTTHydrotestNote(tableIndex);

      // Initialize RelationTestNotes if it doesn't exist
      if (!table.relationTestNotes) {
        table.relationTestNotes = [];
      }

      // Find existing hydrotest note
      const existingHydrotestNoteIndex = table.relationTestNotes.findIndex(
        note => note.type === 'HydrotestNote'
      );

      if (hydrotestNote && hydrotestNote.trim()) {
        // Create or update the hydrotest note object
        const hydrotestNoteViewModel = new PressureTemperatureTableNoteViewModel({
          type: 'HydrotestNote',
          note: hydrotestNote.trim()
        });

        if (existingHydrotestNoteIndex >= 0) {
          // Update existing hydrotest note
          table.relationTestNotes[existingHydrotestNoteIndex] = hydrotestNoteViewModel;
        } else {
          // Add new hydrotest note
          table.relationTestNotes.push(hydrotestNoteViewModel);
        }
      } else {
        // Remove hydrotest note if text is empty
        if (existingHydrotestNoteIndex >= 0) {
          table.relationTestNotes.splice(existingHydrotestNoteIndex, 1);
        }
      }
    });
  }

  /**
   * Apply PTT additional notes from the summary component to RelationAdditionalNotes
   */
  private applyPTTAdditionalNotes(pipeClass: PipeClassViewModel): void {
    if (!this.pipingSummaryComponent || !pipeClass.pressureTemperatureTables) {
      return;
    }

    // Iterate through each pressure temperature table
    pipeClass.pressureTemperatureTables.forEach((table, tableIndex) => {
      // Get the additional notes from the summary component for this table
      const additionalNotes = this.pipingSummaryComponent.getPTTAdditionalNotesForSave(tableIndex);

      if (additionalNotes && additionalNotes.length > 0) {
        // Initialize RelationAdditionalNotes if it doesn't exist
        if (!table.relationAdditionalNotes) {
          table.relationAdditionalNotes = [];
        }

        // Clear existing additional notes and replace with new ones
        table.relationAdditionalNotes = [];

        // Create PressureTemperatureTableNoteViewModel for each additional note
        additionalNotes.forEach(noteText => {
          if (noteText && noteText.trim()) {
            const additionalNoteViewModel = new PressureTemperatureTableNoteViewModel({
              type: 'AdditionalNote',
              note: noteText.trim()
            });
            table.relationAdditionalNotes.push(additionalNoteViewModel);
          }
        });
      } else {
        // If no additional notes, clear the array
        if (table.relationAdditionalNotes) {
          table.relationAdditionalNotes = [];
        }
      }
    });
  }

  /**
   * Apply PTT common notes from the summary component to RelationCommonNotes
   */
  private applyPTTCommonNotes(pipeClass: PipeClassViewModel): void {
    if (!this.pipingSummaryComponent || !pipeClass.pressureTemperatureTables) {
      return;
    }

    // Iterate through each pressure temperature table
    pipeClass.pressureTemperatureTables.forEach((table, tableIndex) => {
      // Get the common notes from the summary component for this table
      const commonNotes = this.pipingSummaryComponent.getPTTCommonNotesForSave(tableIndex);

      if (commonNotes && commonNotes.length > 0) {
        // Initialize RelationCommonNotes if it doesn't exist
        if (!table.relationCommonNotes) {
          table.relationCommonNotes = [];
        }

        // Clear existing common notes and replace with new ones
        table.relationCommonNotes = [];

        // Create CommonNoteViewModel for each common note
        commonNotes.forEach(noteData => {
          if (noteData.codeId && noteData.note) {
            const commonNoteViewModel = new CommonNoteViewModel({
              noteId: noteData.noteId || '',
              codeId: noteData.codeId,
              note: noteData.note,
              isActive: true,
              isArchived: false,
              isReferenced: false,
              updated: new Date()
            });
            table.relationCommonNotes.push(commonNoteViewModel);
          }
        });
      } else {
        // If no common notes, clear the array
        if (table.relationCommonNotes) {
          table.relationCommonNotes = [];
        }
      }
    });
  }



  /**
   * Incrementally add notes to pipeClass.commonNotes ensuring uniqueness (runtime UI sync for common notes tab)
   * Does NOT remove notes (requirement only specifies adding if not existing)
   */
  private addNotesToPipeClassCommon(notes: CommonNoteViewModel[], origin?: string, skipVersionBump?: boolean): void {
    if (!this.currentPipeClassViewModel || !notes || notes.length === 0) {
      return;
    }

    if (!this.currentPipeClassViewModel.commonNotes) {
      this.currentPipeClassViewModel.commonNotes = [];
    }

    const existing = new Set(this.currentPipeClassViewModel.commonNotes.map(n => `${n.noteId || ''}__${n.codeId || ''}`));

    let added = false;
    const addedNotes: string[] = [];
    const skippedNotes: string[] = [];

    notes.forEach(n => {
      if (!n) { return; }
      const key = `${n.noteId || ''}__${n.codeId || ''}`;
      const noteKey = `${n.codeId}-${n.noteId}`;

      if (!existing.has(key)) {
        const noteWithOrigin = new CommonNoteViewModel({
          ...n,
          // Add origin metadata via a custom property (non-standard but usable)
          ...((origin && { origin }) as any)
        });
        this.currentPipeClassViewModel!.commonNotes!.push(noteWithOrigin);
        existing.add(key);
        added = true;
        addedNotes.push(noteKey);
      } else {
        skippedNotes.push(noteKey);
      }
    });
    if (added && !skipVersionBump) {

      this.currentPipeClassViewModel!.commonNotes!.sort((a, b) => {
        const codeA = (a.codeId || '').toLowerCase();
        const codeB = (b.codeId || '').toLowerCase();
        return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
      });

      // Notify UI (common notes tab input updates)
      this.updateObservableDataSource();
      this.forceUIUpdate();
      this.commonNotesVersion++;
    }
  }  /** Remove notes from global list only if they no longer exist in ANY component or valve (additive union semantics with orphan cleanup). */

  private removeNotesFromPipeClassIfOrphan(notes: CommonNoteViewModel[] | undefined): void {
    if (!this.currentPipeClassViewModel || !notes || notes.length === 0 || !this.currentPipeClassViewModel.commonNotes) {
      return;
    }
    const components = (this.currentPipeClassViewModel.componentsInPipeClass || [])
      .filter(comp => comp.component?.group !== 'Valves');
    const valves = this.currentPipeClassViewModel.valves || [];
    const branchTables = this.currentPipeClassViewModel.branchTables || [];
    const pttTables = this.currentPipeClassViewModel.pressureTemperatureTables || [];
    let removed = false;

    notes.forEach(n => {
      if (!n) { return; }
      const keyMatch = (x: CommonNoteViewModel) => (x.noteId || '') === (n.noteId || '') && (x.codeId || '') === (n.codeId || '');
      const noteKey = `${n.codeId}-${n.noteId}`;
      let existsInSummaryProperties = false;
      let existsInComponents = false;
      let existsInValves = false;
      let existsInBranchTables = false;
      let existsInPTT = false;

      const summaryProperties = [
        this.currentPipeClassViewModel.designCode,
        this.currentPipeClassViewModel.ratingClass,
        this.currentPipeClassViewModel.valveTrim,
        this.currentPipeClassViewModel.nominalCorrosionAllowance,
        this.currentPipeClassViewModel.examination,
        this.currentPipeClassViewModel.limitedBy,
        this.currentPipeClassViewModel.temperatureRange,
        this.currentPipeClassViewModel.material,
        this.currentPipeClassViewModel.stressRelief
      ];

      existsInSummaryProperties = summaryProperties.some(prop =>
        prop?.commonNotes?.some(keyMatch)
      );

      existsInComponents = components.some(comp =>
        comp.commonNotes?.some(keyMatch)
      );

      existsInValves = valves.some(valve =>
        valve.commonNotes?.some(keyMatch)
      );

      existsInBranchTables = branchTables.some(bt => {
        const inTableNotes = bt.commonNotes?.some(keyMatch) || bt.relationCommonNotes?.some(keyMatch);

        const inLegendNotes = bt.codes?.some(code =>
          code.commonNotes?.some(keyMatch)
        );

        return inTableNotes || inLegendNotes;
      });

      existsInPTT = pttTables.some(ptt =>
        ptt.relationCommonNotes?.some(keyMatch) || ptt.commonNotes?.some(keyMatch)
      );

      const stillReferenced = existsInSummaryProperties || existsInComponents || existsInValves || existsInBranchTables || existsInPTT;

      // Only remove from unified commonNotes if the note is NOT referenced anywhere
      if (!stillReferenced) {
        const idx = this.currentPipeClassViewModel!.commonNotes!.findIndex(keyMatch);
        if (idx > -1) {
          this.currentPipeClassViewModel!.commonNotes!.splice(idx, 1);
          removed = true;
        }
      }
    });

    if (removed) {

      // Sort common notes after removal to maintain consistent order
      this.currentPipeClassViewModel!.commonNotes!.sort((a, b) => {
        const codeA = (a.codeId || '').toLowerCase();
        const codeB = (b.codeId || '').toLowerCase();
        return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
      });

      this.commonNotesVersion++;
      this.updateObservableDataSource();
      this.updateComponentDataReference();
      this.forceUIUpdate();
    }
  }

  /**
   * Public method to manually trigger save draft (can be called from parent components)
   */
  public savePipeClassDraft(): void {
    this.performSaveDraft();
  }

  navigateToEditMode(): void {
    if (this.pipeClassName) {
      // Propagate the captured return URL so the edit-mode page's back button navigates to the
      // same origin as this view-mode page. Falls back to admin search when no explicit origin
      // was set (e.g. user arrived via a direct URL or bookmark).
      this.router.navigate(['/piping/pipeClass', this.pipeClassName, 'true'], {
        state: { returnUrl: this.returnUrl ?? '/piping/adminSearch' }
      });
    }
  }

  navigateToViewMode(): void {
    if (this.pipeClassName && this.businessUnit) {
      this.router.navigate(['/pipingsummary', this.businessUnit, this.pipeClassName]);
    }
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    this.cdr.markForCheck();
  }

  /**
   * Navigates the user back to the screen they came from.
   * The return URL is captured on load from `history.state.returnUrl` set by the source screen.
   * Falls back to admin search when no explicit origin was provided.
   */
  navigateBack(): void {
    const destination: string = this.returnUrl ?? '/piping/adminSearch';
    // The revisionsAndReleases page reads session storage to pre-select the revisions tab
    // when navigated back to from a pipe class detail view. This side-effect is intentional.
    if (destination === '/piping/revisionsAndReleases') {
      sessionStorage.setItem('pipingNavigationContext', 'pipingTabsParent');
    }
    this.router.navigate([destination]);
  }

  /**
   * Normalize properties structure to ensure correct format for API
   */
  private normalizePropertiesStructure(pipeClass: PipeClassViewModel): void {

    // Normalize designCode
    if (pipeClass.designCode) {
      const designCode = pipeClass.designCode as any;

      // Ensure proper structure - if it's not already a DesignCodeViewModel, create one
      if (!designCode.value && !designCode.edition && typeof designCode === 'string') {
        // Handle case where designCode is just a string
        pipeClass.designCode = new DesignCodeViewModel({
          value: designCode,
          edition: '2020',
          commonNotes: []
        });
      } else {
        // Ensure edition has a default value if missing
        if (!designCode.edition) {
          designCode.edition = '2020';
        }

        // Safely resolve value — if the raw value field is not a string (e.g. the whole
        // object was accidentally assigned on a new pipe class), fall back to undefined
        // so the backend receives a missing field rather than a type-mismatch 400.
        const designCodeValue: string | undefined =
          typeof designCode.value === 'string' ? designCode.value :
          (typeof designCode === 'string' ? designCode : undefined);

        // Recreate as proper DesignCodeViewModel to ensure correct structure
        pipeClass.designCode = new DesignCodeViewModel({
          value: designCodeValue,
          edition: designCode.edition || '2020',
          commonNotes: designCode.commonNotes || []
        });
      }

    }

    // Normalize ratingClass
    if (pipeClass.ratingClass) {
      const ratingClass = pipeClass.ratingClass as any;

      // Ensure proper structure - if it's not already a RatingClassViewModel, create one
      if (!ratingClass.value && !ratingClass.edition && typeof ratingClass === 'string') {
        // Handle case where ratingClass is just a string
        pipeClass.ratingClass = new RatingClassViewModel({
          value: ratingClass,
          edition: '2020',
          commonNotes: []
        });
      } else {
        // Ensure edition has a default value if missing
        if (!ratingClass.edition) {
          ratingClass.edition = '2020';
        }

        // Safely resolve value — if the raw value field is not a string (e.g. the whole
        // object was accidentally assigned on a new pipe class), fall back to undefined
        // so the backend receives a missing field rather than a type-mismatch 400.
        const ratingClassValue: string | undefined =
          typeof ratingClass.value === 'string' ? ratingClass.value :
          (typeof ratingClass === 'string' ? ratingClass : undefined);

        // Recreate as proper RatingClassViewModel to ensure correct structure
        pipeClass.ratingClass = new RatingClassViewModel({
          value: ratingClassValue,
          edition: ratingClass.edition || '2020',
          commonNotes: ratingClass.commonNotes || []
        });
      }

    }

    // Normalize customNotes
    if (pipeClass.customNotes) {

      // Ensure all customNotes are proper PipeClassNoteViewModel instances with valid GUIDs
      pipeClass.customNotes = pipeClass.customNotes.map((note, index) => {
        const validId = note.id && this.isValidGuid(note.id) ? note.id : this.generateGuid();
        return new PipeClassNoteViewModel({
          id: validId,
          note: note.note || ''
        });
      });

    } else {
      pipeClass.customNotes = [];
    }
  }

  /**
   * Validate if string is a valid GUID
   */
  private isValidGuid(str: string): boolean {
    if (!str) return false;
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(str);
  }

  /**
   * Method for child components to notify about data changes
   * This will be called by child components when user makes changes
   */
  public onDataChange(change: PipeClassDataChange): void {
    const changeKey = `${change.section}_${change.field}`;
    this.pendingChanges.set(changeKey, change.value);
  }

  /**
   * Clear all pending changes (useful after successful save)
   */
  private clearPendingChanges(): void {
    this.pendingChanges.clear();
  }

  /**
   * Clean pipe class data to avoid validation errors before sending to API
   */
  private cleanPipeClassData(pipeClass: PipeClassViewModel): void {

    // Clean valves component properties - remove properties with empty Code
    if (pipeClass.valves && pipeClass.valves.length > 0) {
      pipeClass.valves.forEach((valve, valveIndex) => {
        if (valve.componentProperties && valve.componentProperties.length > 0) {
          const originalLength = valve.componentProperties.length;

          // Filter out properties that don't have a valid Code
          valve.componentProperties = valve.componentProperties.filter(prop =>
            prop.code && prop.code.trim() !== ''
          );

          const newLength = valve.componentProperties.length;
          if (originalLength !== newLength) {

          }
        }
      });
    }

  }

  /**
   * Create a proper clone of CommonNoteViewModel array that preserves TypeScript types
   * This replaces JSON.parse(JSON.stringify()) which "eats" types and causes save errors
   */
  private cloneCommonNotes(notes: CommonNoteViewModel[]): CommonNoteViewModel[] {
    return notes.map(note => new CommonNoteViewModel({
      noteId: note.noteId,
      codeId: note.codeId,
      note: note.note,
      isActive: note.isActive,
      isArchived: note.isArchived,
      isReferenced: note.isReferenced,
      updated: note.updated ? new Date(note.updated) : undefined
    }));
  }

  /**
   * Ensure each component maintains its independent notes during save operation
   * Uses type-safe cloning instead of JSON.parse(JSON.stringify()) to preserve TypeScript types
   */
  private ensureComponentNotesIndependence(pipeClass: PipeClassViewModel): void {


    if (pipeClass.componentsInPipeClass && pipeClass.componentsInPipeClass.length > 0) {
      pipeClass.componentsInPipeClass.forEach((component, componentIndex) => {
        if (component.commonNotes && component.commonNotes.length > 0) {
          // Create a type-safe deep copy to ensure each component has its own independent notes
          // This preserves CommonNoteViewModel types and prevents save errors
          component.commonNotes = this.cloneCommonNotes(component.commonNotes);
          // Component notes preserved independently with proper types
        }
      });
    }

    if (pipeClass.valves && pipeClass.valves.length > 0) {
      pipeClass.valves.forEach((valve, valveIndex) => {
        if (valve.commonNotes && valve.commonNotes.length > 0) {
          valve.commonNotes = this.cloneCommonNotes(valve.commonNotes);
        }
      });
    }

  }

  // ========== SUMMARY TAB NOTES METHODS ==========

  onEditSummaryNotes(): void {

    // Open notes selection modal for pipe class level notes
    this.openSummaryNotesModal();
  }

  onSummaryNotesAdded(event: {notes: any[], origin: string}): void {
    if (!event.notes || event.notes.length === 0) { return; }
    const commonNotes = event.notes.map(n => new CommonNoteViewModel({
      noteId: n.id || n.noteId || '',
      codeId: n.code || n.codeId || '',
      note: n.note || n.noteText || '',
      isActive: true,
      isArchived: false,
      isReferenced: false,
      updated: new Date()
    }));
    this.addNotesToPipeClassCommon(commonNotes, event.origin);
  }

  onSummaryNotesRemoved(event: {notes: any[], origin: string}): void {
    if (!event.notes || event.notes.length === 0) {
      return;
    }
    const commonNotes = event.notes.map(n => new CommonNoteViewModel({
      noteId: n.id || n.noteId || '',
      codeId: n.code || n.codeId || '',
      note: n.note || n.noteText || '',
      isActive: true,
      isArchived: false,
      isReferenced: false,
      updated: new Date()
    }));
    this.removeNotesFromPipeClassIfOrphan(commonNotes);
  }

  /**
   * Handle property changes from summary tab (design code, rating class, etc.)
   */
  onSummaryPropertyChanged(event: {field: string, value: any, section: string}): void {
    if (!this.currentPipeClassViewModel) {
      return;
    }


    // Update the current pipe class model directly
    switch (event.field) {
      case 'designCode':
        this.currentPipeClassViewModel.designCode = event.value;
        break;
      case 'ratingClass':
        this.currentPipeClassViewModel.ratingClass = event.value;
        break;
      case 'temperatureRange':
        this.currentPipeClassViewModel.temperatureRange = event.value;
        break;
      default:
        break;
    }

    // Update the observable to trigger change detection
    this.updateObservableDataSource();

    // Mark for change detection
    this.cdr.markForCheck();

    // Track the change for API persistence
    this.onDataChange({
      field: event.field,
      value: event.value,
      section: event.section as 'summary'
    });
  }

  /**
   * Handle general notes changes from summary tab (customNotes)
   */
  onSummaryGeneralNotesChanged(event: {notes: any[], pipeClassId?: string}): void {
    if (!this.currentPipeClassViewModel) {
      return;
    }

    // Update the observable to trigger change detection
    this.updateObservableDataSource();

    // Mark for change detection
    this.cdr.markForCheck();

    // Track the change for API persistence
    this.onDataChange({
      field: 'customNotes',
      value: this.currentPipeClassViewModel.customNotes,
      section: 'summary'
    });
  }

  /**
   * Open notes selection modal for Summary tab (PipeClass level notes)
   */
  private openSummaryNotesModal(): void {
    // IMPORTANT: Use PipeClass level commonNotes (separate from component notes)
    const pipeClassNotes = this.currentPipeClassViewModel?.commonNotes || [];

    // Create a deep copy to avoid modifying shared data
    const safePipeClassNotes = JSON.parse(JSON.stringify(pipeClassNotes));

    // Convert existing CommonNoteViewModel to NoteOption format
    const existingNotes = safePipeClassNotes.map(note => ({
      id: note.noteId || '',
      code: note.codeId || '',
      note: note.note || '',
      category: 'Common',
      selected: true
    }));

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      data: {
        title: 'Summary Common Notes (PipeClass Level)',
        selectedNotes: existingNotes,
        availableNotes: [] // Will be loaded by the modal
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.selectedNotes) {
        // Convert NoteOption back to CommonNoteViewModel format (additive)
        const newSelected = result.selectedNotes.map(note => new CommonNoteViewModel({
          noteId: note.id,
          codeId: note.code,
          note: note.note,
          isActive: true,
          isArchived: false,
          isReferenced: false,
          updated: new Date()
        }));

        if (this.currentPipeClassViewModel) {
          if (!this.currentPipeClassViewModel.commonNotes) {
            this.currentPipeClassViewModel.commonNotes = [];
          }
          const existingSet = new Set(this.currentPipeClassViewModel.commonNotes.map(n => `${n.noteId || ''}__${n.codeId || ''}`));
          let addedAny = false;
          newSelected.forEach(n => {
            const key = `${n.noteId || ''}__${n.codeId || ''}`;
            if (!existingSet.has(key)) {
              this.currentPipeClassViewModel!.commonNotes!.push(n);
              existingSet.add(key);
              addedAny = true;
            }
          });
          if (addedAny) {
            this.commonNotesVersion++; // refresh consumers
            this.addNotesToPipeClassCommon(newSelected, 'summary', true); // skip version bump to avoid double increment
          }
        }

        this.cdr.markForCheck();
        this.onDataChange({
          field: 'summaryCommonNotesAdded',
          value: newSelected,
          section: 'summary'
        });
      }
    });
  }

  // ========== COMPONENT TAB NOTES METHODS ==========

  onEditComponentGroupNotes(groupName: string): void {


    // Open notes selection modal for this component group
    this.openComponentGroupNotesModal(groupName);
  }

  onAddGroupNote(groupName: string): void {
    // This will open the same modal as edit but for adding notes to all components in the group
    // The logic will be the same - opening a notes selection modal
    this.openComponentGroupNotesModal(groupName);
  }

  onRemoveComponent(component: ComponentsInPipeClassViewModel): void {
    if (!this.currentPipeClassViewModel?.componentsInPipeClass) {
      return;
    }

    // Find and remove the component from the array
    const componentIndex = this.currentPipeClassViewModel.componentsInPipeClass.findIndex(
      comp => comp.component.componentId === component.component.componentId &&
              comp.component.group === component.component.group &&
              comp.component.type === component.component.type
    );

    if (componentIndex === -1) {
      return;
    }

  // Capture its notes before removal for orphan evaluation
  const removedComponent = this.currentPipeClassViewModel.componentsInPipeClass[componentIndex];
  const removedNotes = removedComponent.commonNotes || [];
  // Remove the component
  this.currentPipeClassViewModel.componentsInPipeClass.splice(componentIndex, 1);

  // Orphan cleanup for its notes
  this.removeNotesFromPipeClassIfOrphan(removedNotes);
  this.commonNotesVersion++;

    // Update the UI
    this.updateObservableDataSource();
    this.updateComponentDataReference();
    this.forceUIUpdate();

    // Mark that we have pending changes
    this.onDataChange({
      field: 'removedComponent',
      value: {
        componentId: component.component.componentId,
        componentType: component.component.type,
        group: component.component.group
      },
      section: 'components'
    });
  }

  /**
   * Handle NPS update for a component
   */
  onUpdateNps(event: {component: ComponentsInPipeClassViewModel, newNps: string}): void {
    if (!this.currentPipeClassViewModel?.componentsInPipeClass) {
      return;
    }

    // Find the component to update
    const componentIndex = this.currentPipeClassViewModel.componentsInPipeClass.findIndex(
      comp => comp.component.componentId === event.component.component.componentId &&
              comp.component.group === event.component.component.group &&
              comp.component.type === event.component.component.type
    );

    if (componentIndex === -1) {
      return;
    }

    // Update the NPS directly in the currentPipeClassViewModel
    this.currentPipeClassViewModel.componentsInPipeClass[componentIndex].component.nps = event.newNps;

    // Mark component as modified and assign current revision
    const currentComponent = this.currentPipeClassViewModel.componentsInPipeClass[componentIndex];
    currentComponent.changed = true;
    (currentComponent as any).__isModified = true;

    // Assign current revision name if not already set
    if (!currentComponent.lastUpdatedRevision) {
      currentComponent.lastUpdatedRevision = this.currentPipeClassViewModel.fromRevision?.name || '';
    }

    console.log('[onUpdateNps] Component marked as modified:', {
      componentType: currentComponent.component?.type,
      __isModified: (currentComponent as any).__isModified,
      lastUpdatedRevision: currentComponent.lastUpdatedRevision,
      newNps: event.newNps
    });

    // Update the UI
    this.updateObservableDataSource();
    this.updateComponentDataReference();
    this.forceUIUpdate();

    // Mark that we have pending changes
    this.onDataChange({
      field: 'updatedComponentNps',
      value: {
        componentId: event.component.component.componentId,
        componentType: event.component.component.type,
        group: event.component.component.group,
        newNps: event.newNps
      },
      section: 'components'
    });
  }


  /**
   * Handle SCH/RAT update for a component placement
   */
  onUpdateSchRat(event: {component: ComponentsInPipeClassViewModel, newSchRat: string}): void {
    if (!this.currentPipeClassViewModel?.componentsInPipeClass) {
      return;
    }

    const currentComponent = this.currentPipeClassViewModel.componentsInPipeClass.find(
      comp => comp.component.componentId === event.component.component.componentId &&
              comp.component.group === event.component.component.group &&
              comp.component.type === event.component.component.type
    );

    if (!currentComponent) {
      return;
    }

    currentComponent.component.schRat = event.newSchRat;
    currentComponent.changed = true;
    (currentComponent as any).__isModified = true;

    if (!currentComponent.lastUpdatedRevision) {
      currentComponent.lastUpdatedRevision = this.currentPipeClassViewModel.fromRevision?.name || '';
    }

    this.updateObservableDataSource();
    this.updateComponentDataReference();
    this.forceUIUpdate();

    this.onDataChange({
      field: 'updatedComponentSchRat',
      value: {
        componentId: event.component.component.componentId,
        componentType: event.component.component.type,
        group: event.component.component.group,
        newSchRat: event.newSchRat
      },
      section: 'components'
    });
  }

  /**
   * Handle component reordering within a group
   */
  onReorderComponents(event: {groupName: string, components: ComponentsInPipeClassViewModel[]}): void {
    if (!this.currentPipeClassViewModel?.componentsInPipeClass) {
      return;
    }
    this.reorderComponentsByGroupAndDisplayOrder();
    // Update the UI
    this.updateObservableDataSource();
    this.updateComponentDataReference();
    this.forceUIUpdate();

    // Mark that we have pending changes
    this.onDataChange({
      field: 'reorderedComponents',
      value: {
        groupName: event.groupName,
        componentIds: event.components.map(c => c.component.componentId)
      },
      section: 'components'
    });
  }

  onEditGroupNotes(groupName: string): void {
    // This handles editing existing group notes
    this.openComponentGroupNotesModal(groupName);
  }

  /**
   * Open notes selection modal for a component group (shared notes)
   */
  private openComponentGroupNotesModal(groupName: string): void {
    // Find all components in this group to get their shared notes
    const componentsInGroup = this.currentPipeClassViewModel?.componentsInPipeClass?.filter(
      comp => comp.component.group === groupName
    ) || [];

    let groupNotes: CommonNoteViewModel[] = [];

    if (componentsInGroup.length === 0) {
      console.warn('No components found for group:', groupName, '- allowing modal to open for orphaned notes management');
      // Allow opening with empty notes to manage orphaned notes
      groupNotes = [];
    } else {
      // Get the current shared notes for this group using the same logic as the child component
      groupNotes = this.getSharedNotesForGroup(componentsInGroup);
    }

    // Convert existing CommonNoteViewModel to NoteOption format
    const existingNotes = groupNotes.map(note => ({
      id: note.noteId || '',
      code: note.codeId || '',
      note: note.note || '',
      category: 'Common',
      selected: true
    }));

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      data: {
        title: `Component Group Notes for ${groupName}`,
        selectedNotes: existingNotes,
        availableNotes: [] // Will be loaded by the modal
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        // Convert NoteOption back to CommonNoteViewModel format
        const selectedCommonNotes = result.map(note => ({
          noteId: note.id,
          codeId: note.code,
          note: note.note
        }));


        // Apply the selected notes to all components in this group
        this.applyNotesToComponentGroup(groupName, selectedCommonNotes);

        // UPDATE: Use the same event-based system as Summary tab
        // Determine which notes were added and which were removed
        const previousNotes = groupNotes.map(note => ({
          id: note.noteId || '',
          code: note.codeId || '',
          note: note.note || ''
        }));

        const currentNotes = result;

        // Find added notes
        const addedNotes = currentNotes.filter(newNote =>
          !previousNotes.some(oldNote => oldNote.id === newNote.id && oldNote.code === newNote.code)
        );

        // Find removed notes
        const removedNotes = previousNotes.filter(oldNote =>
          !currentNotes.some(newNote => newNote.id === oldNote.id && newNote.code === oldNote.code)
        );

        // Handle bidirectional synchronization directly (we are the parent component)
        if (addedNotes.length > 0) {
          // Convert to CommonNoteViewModel format for internal processing
          const addedAsCommonNotes = addedNotes.map(note => new CommonNoteViewModel({
            noteId: note.id,
            codeId: note.code,
            note: note.note
          }));
          this.addNotesToPipeClassCommon(addedAsCommonNotes, `component-group-${groupName}`);
        }

        if (removedNotes.length > 0) {
          // Convert to CommonNoteViewModel format for internal processing
          const removedAsCommonNotes = removedNotes.map(note => new CommonNoteViewModel({
            noteId: note.id,
            codeId: note.code,
            note: note.note
          }));
          this.removeNotesFromPipeClassIfOrphan(removedAsCommonNotes);
        }

        // Update UI
        this.updateObservableDataSource();
        this.updateComponentDataReference();
        this.forceUIUpdate();

        this.onDataChange({
          field: 'componentGroupNotes',
          value: { groupName, notes: selectedCommonNotes },
          section: 'components'
        });
      } else {
      }
    });
  }

  /**
   * Get notes that are shared among all components in a group
   */
  private getSharedNotesForGroup(components: ComponentsInPipeClassViewModel[]): any[] {
    if (components.length === 0) return [];

    // Get the notes from the first component as the base set
    const firstComponentNotes = components[0]?.commonNotes || [];

    if (components.length === 1) {
      return firstComponentNotes;
    }

    // Find notes that exist in ALL components in the group
    const sharedNotes = firstComponentNotes.filter(note => {
      return components.every(component => {
        return component.commonNotes?.some(compNote =>
          compNote.noteId === note.noteId &&
          compNote.codeId === note.codeId
        );
      });
    });

    return sharedNotes;
  }

  /**
   * Apply selected notes to all components in a group
   */
  private applyNotesToComponentGroup(groupName: string, selectedNotes: any[]): void {

    if (!this.currentPipeClassViewModel?.componentsInPipeClass) {
      return;
    }

    // Find all components in the group
    const componentsInGroup = this.currentPipeClassViewModel.componentsInPipeClass.filter(
      component => component.component.group === groupName
    );


    if (componentsInGroup.length === 0) {
      return;
    }

    // Get the current shared notes for the group
    const currentSharedNotes = this.getSharedNotesForGroup(componentsInGroup);

    // Find notes that are being added (exist in selectedNotes but not in current shared notes)
    const notesToAdd = selectedNotes.filter(selectedNote =>
      !currentSharedNotes.some(currentNote =>
        currentNote.noteId === selectedNote.noteId && currentNote.codeId === selectedNote.codeId
      )
    );

    // Find notes that are being removed (exist in current shared notes but not in selectedNotes)
    const notesToRemove = currentSharedNotes.filter(currentNote =>
      !selectedNotes.some(selectedNote =>
        selectedNote.noteId === currentNote.noteId && selectedNote.codeId === currentNote.codeId
      )
    );

    // Apply changes to all components in the group
    componentsInGroup.forEach((component, index) => {

      // Initialize commonNotes if not exists
      if (!component.commonNotes) {
        component.commonNotes = [];
      }

      // Add new notes
      notesToAdd.forEach(noteToAdd => {
        // Check if the note doesn't already exist in this component
        const noteExists = component.commonNotes?.some(existingNote =>
          existingNote.noteId === noteToAdd.noteId && existingNote.codeId === noteToAdd.codeId
        );

        if (!noteExists) {
          component.commonNotes.push(new CommonNoteViewModel({
            noteId: noteToAdd.noteId,
            codeId: noteToAdd.codeId,
            note: noteToAdd.note
          }));
        }
      });

      // Remove notes
      if (component.commonNotes) {
        component.commonNotes = component.commonNotes.filter(existingNote =>
          !notesToRemove.some(noteToRemove =>
            noteToRemove.noteId === existingNote.noteId && noteToRemove.codeId === existingNote.codeId
          )
        );


      }

      // Mark component as modified if notes were added or removed
      if (notesToAdd.length > 0 || notesToRemove.length > 0) {
        component.changed = true;
        (component as any).__isModified = true;

        // Assign current revision name if not already set
        if (!component.lastUpdatedRevision && this.currentPipeClassViewModel?.fromRevision?.name) {
          component.lastUpdatedRevision = this.currentPipeClassViewModel.fromRevision.name;
        }
      }
    });

  }

  onEditIndividualComponentNotes(component: ComponentsInPipeClassViewModel): void {


    // Open notes selection modal for this specific component
    this.openComponentNotesModal(component);
  }

  /**
   * Open notes selection modal for a specific component
   */
  private openComponentNotesModal(component: ComponentsInPipeClassViewModel): void {
    const componentNotes = component.commonNotes ? JSON.parse(JSON.stringify(component.commonNotes)) : [];

    const existingNotes = componentNotes.map(note => ({
      id: note.noteId || '',
      code: note.codeId || '',
      note: note.note || '',
      category: 'Common',
      selected: true
    }));

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      data: {
        title: `Notes for Component: ${component.component.type} (ID: ${component.component.componentId})`,
        selectedNotes: existingNotes,
        availableNotes: []
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) {
        // Store previous notes for comparison
        const previousNotes = (component.commonNotes || []).map(note => ({
          id: note.noteId || '',
          code: note.codeId || '',
          note: note.note || ''
        }));


        const selectedCommonNotes: CommonNoteViewModel[] = result.map(note => {
          return new CommonNoteViewModel({
            noteId: note.id || note.noteId || '',
            codeId: note.code || note.codeId || '',
            note: note.note || note.noteText || '',
            isActive: true,
            updated: new Date(),
            isArchived: false,
            isReferenced: false
          });
        });

        // Update component notes
        component.commonNotes = selectedCommonNotes;

        // Mark component as modified and assign current revision
        component.changed = true;
        (component as any).__isModified = true;

        // Assign current revision name if not already set
        if (!component.lastUpdatedRevision && this.currentPipeClassViewModel?.fromRevision?.name) {
          component.lastUpdatedRevision = this.currentPipeClassViewModel.fromRevision.name;
        }

        // Handle bidirectional synchronization
        // Find added notes
        const addedNotes = result.filter(newNote =>
          !previousNotes.some(oldNote => oldNote.id === newNote.id && oldNote.code === newNote.code)
        );

        // Find removed notes
        const removedNotes = previousNotes.filter(oldNote =>
          !result.some(newNote => newNote.id === oldNote.id && newNote.code === oldNote.code)
        );
        if (addedNotes.length > 0) {
          const addedAsCommonNotes = addedNotes.map(note => new CommonNoteViewModel({
            noteId: note.id,
            codeId: note.code,
            note: note.note
          }));
          this.addNotesToPipeClassCommon(addedAsCommonNotes, `component-${component.component.componentId}`);
        }

        if (removedNotes.length > 0) {
          const removedAsCommonNotes = removedNotes.map(note => new CommonNoteViewModel({
            noteId: note.id,
            codeId: note.code,
            note: note.note
          }));
          this.removeNotesFromPipeClassIfOrphan(removedAsCommonNotes);
        }

        this.updateObservableDataSource();
        this.updateComponentDataReference();
        this.forceUIUpdate();

        this.onDataChange({
          field: 'individualComponentNotes',
          value: {
            componentId: component.component.componentId,
            componentType: component.component.type,
            notes: selectedCommonNotes
          },
          section: 'components'
        });

        this.debugIndividualComponentNotes(component);
      } else {
      }
    });
  }

  debugNoteSeparation(): void {
  }

  /**
   * Debug method to verify individual component note management
   */
  debugIndividualComponentNotes(component: ComponentsInPipeClassViewModel): void {
    // Removed debug logs - functionality maintained for component note separation
  }

  public initializeIndependentComponentNotes(): void {
    if (!this.currentPipeClassViewModel?.componentsInPipeClass) {
      return;
    }

    this.currentPipeClassViewModel.componentsInPipeClass.forEach((component, index) => {
      if (!component.commonNotes) {
        component.commonNotes = [];
      } else if (component.commonNotes.length > 0) {
        // Use type-safe cloning instead of JSON.parse(JSON.stringify())
        // This preserves CommonNoteViewModel types and prevents change detection issues
        component.commonNotes = this.cloneCommonNotes(component.commonNotes);
      }
    });

    this.cdr.markForCheck();
  }

  /**
   * Normalises the `rating` and `ends` fields on every valve in `pipeClass` by resolving
   * the override stored in the matching `ComponentsInPipeClassViewModel` entry.
   *
   * The backend stores the per-pipe-class overrides in `componentsInPipeClass[].component.schRat`
   * and `.end`, but returns the raw catalog values in `valves[].rating` / `.ends`. This method
   * merges them so that both the current and the previous pipe class present the same resolved
   * values before the valve tab renders them.
   *
   * Called for the current pipe class on load and after every save, and for the previous pipe
   * class immediately after it is fetched for compare mode.
   */
  private normalizeValveOverrides(pipeClass: PipeClassViewModel): void {
    if (!pipeClass?.valves || pipeClass.valves.length === 0) {
      return;
    }

    pipeClass.valves.forEach(valve => {
      if (!valve.components || valve.components.length === 0) {
        return;
      }

      const valveComponent = valve.components[0];
      if (!valveComponent || !valveComponent.componentId) {
        return;
      }

      let componentInPipeClass: ComponentsInPipeClassViewModel | undefined;
      if (pipeClass.componentsInPipeClass) {
        // Use componentInPipeClassId for exact match to avoid overwriting values
        // when multiple ComponentsInPipeClassViewModel entries share the same componentId
        componentInPipeClass = valve.componentInPipeClassId
          ? pipeClass.componentsInPipeClass.find(comp => comp.id === valve.componentInPipeClassId)
          : pipeClass.componentsInPipeClass.find(comp => comp.component?.componentId === valveComponent.componentId);
      }

      const correctRating = componentInPipeClass?.component?.schRat || valveComponent.schRat || valve.rating || '';
      const correctEnds = componentInPipeClass?.component?.end || valveComponent.end || valve.ends || '';
      const correctNps = componentInPipeClass?.component?.nps || valveComponent.nps || valve.nps || '';

      valve.rating = correctRating;
      valve.ends = correctEnds;
      valve.nps = correctNps;

      valveComponent.schRat = correctRating;
      valveComponent.end = correctEnds;
      valveComponent.nps = correctNps;

      if (componentInPipeClass?.component) {
        componentInPipeClass.component.schRat = correctRating;
        componentInPipeClass.component.end = correctEnds;
        componentInPipeClass.component.nps = correctNps;
      }
    });
  }

  /**
   * Guarantees every valve carries the correct `componentInPipeClassId` (the unambiguous key
   * `ResolveValveComponentInPipeClass` uses first) and a `displayOrder` matching its actual
   * position in the list (the tie-breaker used when `componentInPipeClassId` is missing).
   *
   * Without this, a valve saved with `componentInPipeClassId` unset/empty forces the backend to
   * fall back to matching by `ComponentId + DisplayOrder`, which is ambiguous whenever the same
   * catalog valve is used more than once across releases/pipe classes.
   *
   * Called before every save, immediately before `normalizeValveOverrides` so the override
   * lookup there also benefits from a resolved id.
   */
  private ensureValveComponentInPipeClassIds(pipeClass: PipeClassViewModel | null): void {
    if (!pipeClass?.valves || pipeClass.valves.length === 0) {
      return;
    }

    pipeClass.valves.forEach((valve, index) => {
      // Keep displayOrder in sync with the row's actual position -- this is the fallback
      // tie-breaker on the backend when componentInPipeClassId is absent, so it must always
      // reflect the row currently being edited, not a stale value from a previous load/reorder.
      valve.displayOrder = index;

      // Already resolved -- nothing to do.
      if (valve.componentInPipeClassId) {
        return;
      }

      const valveComponent = valve.components?.[0];
      if (!valveComponent?.componentId || !pipeClass.componentsInPipeClass) {
        return;
      }

      // Fallback resolution mirrors app-valve-tab's getComponentsInPipeClassId: prefer an exact
      // match on componentId + componentClassificationId, then fall back to componentId alone.
      let match: ComponentsInPipeClassViewModel | undefined;
      if (valveComponent.componentClassificationId) {
        match = pipeClass.componentsInPipeClass.find(
          c => c.component?.componentId === valveComponent.componentId
            && c.component?.componentClassificationId === valveComponent.componentClassificationId
        );
      }
      if (!match) {
        match = pipeClass.componentsInPipeClass.find(
          c => c.component?.componentId === valveComponent.componentId
        );
      }
      if (match?.id) {
        valve.componentInPipeClassId = match.id;
      }
    });
  }

  /** Applies `normalizeValveOverrides` to the current pipe class and triggers change detection. */
  private ensureValveDataConsistency(): void {
    this.ensureValveComponentInPipeClassIds(this.currentPipeClassViewModel);
    this.normalizeValveOverrides(this.currentPipeClassViewModel);
    this.cdr.markForCheck();
  }

  // ── Catalog validation ───────────────────────────────────────────────────

  /**
   * Fires two parallel batch requests — one for components, one for valves — to check
   * whether the catalog entries referenced by this pipe class are still the latest versions.
   * Results are stored in `componentValidationResults` and `valveValidationResults` Maps
   * so child components can look them up by ID.
   *
   * Called whenever a pipe class loads, regardless of revision status. The upgrade action
   * in the UI is gated by `canEdit$`, so the indicator is informational in read-only mode.
   */
  private validateCatalogFreshness(pipeClass: PipeClassViewModel): void {
    // Collect unique component IDs from the componentsInPipeClass list,
    // excluding valve-associated entries (valves have their own validation endpoint).
    const valveComponentIds = new Set<string>(
      (pipeClass.valves || [])
        .map(v => v.componentInPipeClassId)
        .filter((id): id is string => !!id)
    );
    const componentIds: string[] = (pipeClass.componentsInPipeClass || [])
      .filter(c => c.component?.componentId && !valveComponentIds.has(c.id))
      .map(c => c.component.componentId)
      .filter((id, i, arr) => arr.indexOf(id) === i); // deduplicate

    const valveIds: string[] = (pipeClass.valves || [])
      .map(v => v.id)
      .filter((id): id is string => !!id);

    // Fire both requests in parallel; a failure in either must not block the other.
    const components$ = componentIds.length > 0
      ? this.esraPipingAPIClient.validateAndGetLatestComponents(componentIds).pipe(catchError(() => of([])))
      : of([]);
    const valves$ = valveIds.length > 0
      ? this.esraPipingAPIClient.validateAndGetLatestValves(valveIds).pipe(catchError(() => of([])))
      : of([]);

    forkJoin({ components: components$, valves: valves$ })
      .pipe(indicate(this.catalogValidationLoading$), first())
      .subscribe({
        next: ({ components, valves }) => {
          this.componentValidationResults = new Map(
            (components as ComponentValidationResult[]).map(r => [r.componentId, r])
          );
          this.valveValidationResults = new Map(
            (valves as ValveValidationResult[]).map(r => [r.valveId, r])
          );
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Replace a component's catalog entry in-place with the latest version returned by the
   * validation API.
   *
   * The replacement follows the same field-mapping convention used when adding a component
   * via the catalog modal (`addSelectedComponentsToPipeClass`):
   *   - All catalog fields (description, schRat, end, code, material, etc.) come from
   *     `latestVersion`.
   *   - Per-pipe-class fields that the user controls are preserved from the existing row:
   *     `group` (keeps the component in the right tab), `nps` / `npsFrom` / `npsTo`
   *     (user-edited), and `commonNotes`.
   *   - `componentClassificationId`, `displayOrder`, and `lastUpdatedRevision` are carried
   *     forward so the row stays in its original position.
   *
   * Using `ComponentViewModel.fromJS()` ensures `init()` and `toJSON()` are present on the
   * replacement, avoiding "missing properties" TS errors during save.
   *
   * Called via `(replaceComponentEvent)` emitted from `app-pipe-component-details`.
   */
  onReplaceComponent(event: { current: ComponentsInPipeClassViewModel; latestVersion: ComponentViewModel }): void {
    if (!this.currentPipeClassViewModel?.componentsInPipeClass) { return; }

    // Use the join-table id for a unique, unambiguous row lookup.
    // Matching by componentId + group + type is non-unique when the same catalog component
    // appears multiple times in a pipe class (e.g., the same pipe added for different NPS ranges).
    const idx = this.currentPipeClassViewModel.componentsInPipeClass.findIndex(
      c => c.id === event.current.id
    );
    if (idx === -1) { return; }

    const existing = this.currentPipeClassViewModel.componentsInPipeClass[idx];
    const existingComponent = existing.component;

    // Capture the old ID before patching so we can clean up the validation map below.
    const oldId: string | undefined = existingComponent?.componentId;

    // Patch the existing ComponentViewModel in-place instead of replacing it.
    //
    // This mirrors the field-mapping strategy used by addSelectedComponentsToPipeClass (the
    // add-component modal close handler): only catalog-owned fields come from latestVersion;
    // per-pipe-class fields that the user controls — group, nps, componentClassificationId,
    // specialRequirements — are left untouched.
    //
    // Replacing the object reference (existing.component = newObj) would break the reference
    // that pipe-component-details holds on the row it rendered, causing the row to vanish
    // from the UI even though the ComponentsInPipeClassViewModel is still in the array.
    existingComponent.componentId       = event.latestVersion.componentId;
    existingComponent.type              = event.latestVersion.type;
    existingComponent.typeCode          = event.latestVersion.typeCode;
    existingComponent.code              = event.latestVersion.code;
    existingComponent.description       = event.latestVersion.description;
    existingComponent.schRat            = event.latestVersion.schRat            || '';
    existingComponent.sch2              = event.latestVersion.sch2              || '';
    existingComponent.material          = event.latestVersion.material          || '';
    existingComponent.astm              = event.latestVersion.astm              || '';
    existingComponent.construction      = event.latestVersion.construction      || '';
    existingComponent.designStandard    = event.latestVersion.designStandard    || '';
    existingComponent.end               = event.latestVersion.end               || '';
    existingComponent.notes             = event.latestVersion.notes             || '';
    existingComponent.isActive          = event.latestVersion.isActive          ?? true;
    existingComponent.isReferenced      = event.latestVersion.isReferenced      ?? false;
    existingComponent.isArchived        = event.latestVersion.isArchived        ?? false;
    existingComponent.fromRevisionId    = event.latestVersion.fromRevisionId    ?? 0;
    existingComponent.toRevisionId      = event.latestVersion.toRevisionId      ?? 0;
    // Preserved (not overwritten): componentClassificationId, group, nps, npsFrom, npsTo,
    // specialRequirements — these are per-pipe-class values the user controls.
    //
    // schRat and end are NOT user-editable per the UI (no edit icon). They come from the new
    // catalog entry and are stored via HandlePipeClassFreeTextOverrides as PipeClassFreeTextOverride
    // rows (AllowedNames includes "schrat" and "end"). The condition is: create a free-text
    // override when the view-model value differs from the catalog's own "schrat"/"end" property.
    // Since the catalog may store these under a different property name (e.g. "description_ends"
    // rather than "end"), componentValue is often null — so the condition is met and the override
    // is created. On reload, PipingDataMapping Priority 1 finds it via DataMappingHelper
    // case-insensitive match on "end" / "schrat", making the value visible.

    existing.changed = true;
    // Use Revision.Name (short label, e.g. "1") to match what the backend stores in
    // ComponentInPipeClass.LastUpdatedRevision.Name. The fromRevision.name field holds the
    // ReleaseFullName ("PIM-SU-5112-1"), so we extract the last hyphen-segment to match the
    // format the getLastRevisionSegment comparison expects.
    const revFullName: string = this.currentPipeClassViewModel.fromRevision?.name || '';
    existing.lastUpdatedRevision = revFullName.split('-').pop() || revFullName || existing.lastUpdatedRevision;

    // Clear the join-table ID so the backend treats this as a remove + insert rather than
    // an in-place update.  UpdatePipeClassComponents matches rows by ComponentsInPipeClassViewModel.id:
    //   - The old row (no longer present with that ID) is deleted.
    //   - The patched entry (id === undefined → not matched) is passed to InsertNewComponentInPipeClass,
    //     which fetches the new catalog Component entity by ComponentId and creates the FK correctly.
    // UpdateComponentInPipeClass only updates overrides/notes — it never reassigns the Component FK,
    // so keeping the same ID would silently discard the catalog upgrade.
    existing.id = undefined;

    // Mark this component as current in the validation map so the indicator disappears.
    const newId: string = existingComponent.componentId;
    const updatedResult = new ComponentValidationResult();
    updatedResult.componentId = newId;
    updatedResult.isCurrent = true;
    this.componentValidationResults.set(newId, updatedResult);
    // Remove the old entry to prevent a stale indicator when the componentId changed.
    if (oldId && oldId !== newId) {
      this.componentValidationResults.delete(oldId);
    }

    this.updateObservableDataSource();
    this.updateComponentDataReference();
    this.forceUIUpdate();
    this.onDataChange({
      field: 'replacedComponent',
      value: { componentId: newId },
      section: 'components'
    });
  }

  /**
   * Replace a valve's catalog data in-place with the latest version while preserving
   * per-pipe-class overrides (tag, notes, displayOrder, componentInPipeClassId).
   *
   * Called via `(replaceValveEvent)` emitted from `app-valve-tab`.
   */
  onReplaceValve(event: { valve: ValveViewModel; latestVersion: ValveViewModel }): void {
    if (!this.currentPipeClassViewModel?.valves) { return; }

    const idx = this.currentPipeClassViewModel.valves.findIndex(v => v.id === event.valve.id);
    if (idx === -1) { return; }

    const current = this.currentPipeClassViewModel.valves[idx];
    const currentComponentInPipeClassId: string | undefined = current.componentInPipeClassId;

    // Build the replacement via fromJS so init()/toJSON() are present (same as API objects).
    const merged = ValveViewModel.fromJS(event.latestVersion.toJSON());

    // Preserve per-pipe-class fields that the catalog API does not own.
    // merged.tag = current.tag;                                   // user-assigned tag
    merged.componentInPipeClassId = current.componentInPipeClassId;
    merged.displayOrder = current.displayOrder;
    merged.commonNotes = current.commonNotes;
    merged.nps = current.nps;

    // Prefer shortDescription first so non-manual replacements keep the canonical
    // short description instead of a broader pipe-class description value.
    merged.description = merged.shortDescription || merged.pipeClassDescription || merged.description || current.description;

    if (merged.components && merged.components.length > 0) {
      merged.components[0].description = merged.description;
      merged.components[0].nps = merged.nps;
    }

    merged.changed = true;
    this.currentPipeClassViewModel.valves[idx] = merged;

    // Update the matching ComponentsInPipeClassViewModel entry.
    //
    // Valves have two parallel representations: ValveViewModel (in .valves[]) and
    // ComponentsInPipeClassViewModel (in .componentsInPipeClass[]) which carries the
    // editable overrides — ends, rating, and NPS. UpdatePipeClassValves only manages
    // the PipeClass.Valves many-to-many association by Valve.Id and explicitly does
    // NOT update overrides (the backend comment reads: "do not edit valves — the
    // possible editable values for a valve are handled by the components list").
    //
    // After a valve upgrade the new Valve entity has a different Components[0].Id.
    // The backend re-joins ComponentInPipeClass to its valve on reload via:
    //   valve.Components[0].Id == componentInPipeClass.ComponentId
    // If the ComponentInPipeClass row still has the old ComponentId, no valve
    // matches that query and AddFreeTextOverrideValues throws on the next GET.
    //
    // Fix: find the matching ComponentsInPipeClassViewModel (by the preserved
    // componentInPipeClassId), update its component.componentId to the new valve's
    // component, and clear its join-table id. Clearing the id causes
    // UpdatePipeClassComponents to remove the old row and call
    // InsertNewComponentInPipeClass (which correctly links the new Component FK)
    // instead of UpdateComponentInPipeClass (which never updates the Component FK).
    const newValveComponent: ComponentViewModel | undefined = event.latestVersion.components?.[0];
    const cipc: ComponentsInPipeClassViewModel | undefined = this.currentPipeClassViewModel.componentsInPipeClass?.find(
      c => c.id === currentComponentInPipeClassId
    );
    if (cipc?.component && newValveComponent?.componentId) {
      const replacementComponent: ComponentViewModel = ComponentViewModel.fromJS(cipc.component.toJSON());

      replacementComponent.componentId = newValveComponent.componentId;
      // Reflect the new catalog values so HandlePipeClassDictionaryOverrides
      // computes the correct override delta against the new component's properties.
      replacementComponent.schRat = merged.rating || '';
      replacementComponent.end = merged.ends || '';
      replacementComponent.description = merged.description || '';
      // NPS range is user-controlled — preserve the existing values.
      replacementComponent.nps = current.nps || cipc.component.nps;

      const replacementCommonNotes: CommonNoteViewModel[] = cipc.commonNotes
        ? this.cloneCommonNotes(cipc.commonNotes)
        : [];

      const replacementCipc: ComponentsInPipeClassViewModel = new ComponentsInPipeClassViewModel({
        component: replacementComponent,
        commonNotes: replacementCommonNotes,
        displayOrder: cipc.displayOrder,
        lastUpdatedRevision: cipc.lastUpdatedRevision,
        changed: true
      });

      const componentsInPipeClass = this.currentPipeClassViewModel.componentsInPipeClass || [];
      const cipcIndex = componentsInPipeClass.findIndex(c => c.id === currentComponentInPipeClassId);
      if (cipcIndex >= 0) {
        componentsInPipeClass[cipcIndex] = replacementCipc;
      }

      // Keep replacement semantics aligned with add-valve: new row has no join-table ID
      // until the backend inserts it on save.
      merged.componentInPipeClassId = undefined;
    }


    // Mark as current so the indicator disappears.
    const updatedValveResult = new ValveValidationResult();
    updatedValveResult.valveId = event.valve.id;
    updatedValveResult.isCurrent = true;
    this.valveValidationResults.set(event.valve.id, updatedValveResult);

    this.ensureValveDataConsistency();
    this.updateObservableDataSource();
    this.forceUIUpdate();
    this.onDataChange({
      field: 'replacedValve',
      value: { id: event.valve.id },
      section: 'valves'
    });
  }

  private forceUIUpdate(): void {

    // Mark for check first
    this.cdr.markForCheck();

    // Immediate detection
    this.cdr.detectChanges();

    // Force a complete refresh of the data reference
    if (this.currentPipeClassViewModel?.componentsInPipeClass) {
      // Create a new array reference to trigger change detection in child components
      this.currentPipeClassViewModel.componentsInPipeClass = [
        ...this.currentPipeClassViewModel.componentsInPipeClass
      ];

      // Force deep update of commonNotes arrays to ensure child components detect changes
      this.currentPipeClassViewModel.componentsInPipeClass.forEach(comp => {
        if (comp.commonNotes) {
          comp.commonNotes = [...comp.commonNotes];
        }
      });
    }

    // Update the observable
    this.updateObservableDataSource();

    // Multiple async update cycles to ensure all child components catch the changes
    setTimeout(() => {
      this.cdr.detectChanges();
      this.cdr.markForCheck();
    }, 0);

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 50);

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);

  }

  private updateComponentDataReference(): void {
    // Don't create new array references - preserve object instances for proper API synchronization
    // Angular change detection will work with direct object property changes
    if (this.currentPipeClassViewModel?.componentsInPipeClass) {
      // Force change detection by updating the observable directly
      this.pipeClassViewModel$ = of(this.currentPipeClassViewModel);
    }
  }

  private updateObservableDataSource(): void {
    if (this.currentPipeClassViewModel) {
      // Update the observable with the current model instance
      // This ensures the template gets the latest currentPipeClassViewModel with all changes
      this.pipeClassViewModel$ = of(this.currentPipeClassViewModel);
    }
  }

  // ========== COMPONENT CATALOG MODAL METHODS ==========

  /**
   * Open component catalog modal to add components to a specific group
   */
  onAddComponentsToGroup(groupName: string): void {
    // Get currently selected components for this group to pre-select them in the modal
    const currentlySelectedComponents: ComponentOption[] = [];

    if (this.currentPipeClassViewModel?.componentsInPipeClass) {
      this.currentPipeClassViewModel.componentsInPipeClass
        .filter(c => c.component.group === groupName)
        .forEach(c => {
          currentlySelectedComponents.push({
            id: c.component.componentId || '',
            code: c.component.code || '',
            group: c.component.group || '',
            type: c.component.type || '',
            description: c.component.description || '',
            material: c.component.material || '',
            astm: c.component.astm || '',
            construction: c.component.construction || '',
            designStandard: c.component.designStandard || '',
            scH1RAT: c.component.schRat || '',
            scH2: c.component.sch2 || '',
            ends: c.component.end || '',
            selected: true
          });
        });
    }

    const dialogData: ComponentCatalogSelectionData = {
      title: `Add Components to ${groupName}`,
      groupFilter: groupName, // Pre-filter by the selected group
      selectedComponents: currentlySelectedComponents
    };

    const dialogRef = this.dialog.open(ComponentCatalogModalComponent, {
      // make the modal very large but responsive
      width: '95vw',
      maxWidth: '2000px',
      height: '95vh',
      maxHeight: '98vh',
      panelClass: 'xlarge-component-catalog-modal',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((selectedComponents: ComponentOption[]) => {
      if (selectedComponents && selectedComponents.length > 0) {
        this.addSelectedComponentsToPipeClass(selectedComponents, groupName);
      }
    });
  }

  /**
   * Add selected components from catalog to the pipe class
   */
  private addSelectedComponentsToPipeClass(selectedComponents: ComponentOption[], groupName: string): void {
    if (!this.currentPipeClassViewModel) {
      return;
    }


    // Initialize componentsInPipeClass if it doesn't exist
    if (!this.currentPipeClassViewModel.componentsInPipeClass) {
      this.currentPipeClassViewModel.componentsInPipeClass = [];
    }


    // Avoid adding duplicates: exclude components already present in the pipe class
    // Check by componentId and group to prevent same component being added to same group
    const existingComponents = this.currentPipeClassViewModel.componentsInPipeClass.map(c => ({
      id: c.component.componentId,
      group: c.component.group,
      type: c.component.type
    }));


    const filteredSelection = selectedComponents.filter(sc => {
      const isDuplicate = existingComponents.some(existing =>
        existing.id === sc.id &&
        existing.group === groupName &&
        existing.type === sc.type
      );
      if (isDuplicate) {
      }
      return !isDuplicate;
    });


    if (filteredSelection.length === 0) {
      return;
    }

    // Get current revision name for new components
    const currentRevisionName = this.currentPipeClassViewModel.fromRevision?.name || '';

    const newComponents: ComponentsInPipeClassViewModel[] = filteredSelection.map((component, index) => {

      const componentViewModel = new ComponentViewModel({
        componentId: component.id,
        componentClassificationId: this.generateGuid(), // Generate valid GUID
        group: groupName, // Use the actual group name, not the display name
        type: component.type,
        code: component.code,
        notes: '',
        nps: '', // NPS is typically filled manually by user
        schRat: component.scH1RAT || '', // Map scH1RAT to schRat
        sch2: component.scH2 || '',
        material: component.material || '',
        astm: component.astm || '',
        construction: component.construction || '',
        designStandard: component.designStandard || '',
        specialRequirements: '',
        end: component.ends || '',
        description: component.description,
        fromRevisionId: 0,
        toRevisionId: 0,
        isActive: true,
        isReferenced: false,
        isArchived: false
      });

      const newComp = new ComponentsInPipeClassViewModel({
        component: componentViewModel,
        commonNotes: [], // Initialize with empty array so "Add Note" functionality works
        displayOrder: (this.currentPipeClassViewModel.componentsInPipeClass.length + index + 1),
        lastUpdatedRevision: currentRevisionName
      }) as any;
      // mark as newly added so the UI can show an icon
      newComp.__isNew = true;
      return newComp as ComponentsInPipeClassViewModel;
    });


    // Add new components to the existing list
    this.currentPipeClassViewModel.componentsInPipeClass.push(...newComponents);

    //Reorder components after addition
    this.reorderComponentsByGroupAndDisplayOrder();

    // Update the UI
    this.updateObservableDataSource();
    this.updateComponentDataReference();
    this.forceUIUpdate();


    // Mark that we have pending changes
    this.onDataChange({
      field: 'addedComponents',
      value: {
        groupName,
        addedComponents: newComponents
      },
      section: 'components'
    });
  }

  // ========== BRANCH TABLE MODAL METHODS ==========

  /**
   * Open branch table dictionary modal to add tables to the pipe class
   */
  onAddBranchTablesToGroup(groupName: string): void {
    // Extract IDs of currently selected tables to exclude them from the modal
    const currentIds = this.branchTableViewModelList?.map(t => t.branchTableId) || [];

    this.branchTableViewModelList?.forEach(t => {
      console.log(`    • ${t.name} (ID: ${t.branchTableId})`);
    });

    const dialogData: BranchDictionaryModalData = {
      excludeIds: currentIds,
      existingTables: []
    };

    const dialogRef = this.dialog.open(BranchDictionaryModalComponent, {
      width: '1200px',
      maxWidth: '95vw',
      height: '90vh',
      maxHeight: '95vh',
      panelClass: 'large-modal',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((selectedTables: BranchTableViewModel[]) => {
      if (selectedTables !== undefined) { // null/undefined means canceled, empty array means deselect all
        this.addSelectedBranchTablesToPipeClass(selectedTables);
      }
    });
  }

  /**
   * Add newly selected branch tables from modal (modal now only returns NEW tables to add)
   */
  addSelectedBranchTablesToPipeClass(selectedTables: BranchTableViewModel[]): void {
    // Initialize branchTableViewModelList if it doesn't exist
    if (!this.branchTableViewModelList) {
      this.branchTableViewModelList = [];
    }

    // Modal now excludes already-selected tables, so selectedTables contains only NEW tables to add

    selectedTables.forEach(table => {
      // Double-check the table is not already in the list (safety check)
      const existingTable = this.branchTableViewModelList.find(bt => bt.branchTableId === table.branchTableId);

      if (!existingTable) {
        // Always create a fresh copy of the table - preserve commonNotes from dictionary

        const branchTableForPipeClass = new BranchTableViewModel({
          branchTableId: table.branchTableId,
          name: table.name,
          type: table.type,
          isActive: table.isActive,
          cells: table.cells,
          codes: table.codes,
          commonNotes: table.commonNotes || [], // Preserve common notes from dictionary
          relationCommonNotes: [], // Initialize empty relation notes (specific to this pipe class)
          relationAdditionalNotes: [], // Initialize empty additional notes (specific to this pipe class)
          isReferenced: table.isReferenced,
          pipeClassIds: table.pipeClassIds
        });

        this.branchTableViewModelList.push(branchTableForPipeClass);
      }
    });

    // Force array reference change to trigger ngOnChanges in child components
    const previousLength = this.branchTableViewModelList.length;
    this.branchTableViewModelList = [...this.branchTableViewModelList];
    if (this.currentPipeClassViewModel) {
      this.currentPipeClassViewModel.branchTables = this.branchTableViewModelList;
    }

    // Force change detection immediately and schedule additional checks
    this.cdr.detectChanges();

    // Use requestAnimationFrame for better rendering timing
    requestAnimationFrame(() => {
      this.cdr.detectChanges();
    });

    // Final check after a short delay to ensure all components are rendered
    setTimeout(() => {
      this.cdr.detectChanges();

      // Trigger validation of all branch tables to check if sync is needed
      if (this.branchTablesComponent && selectedTables.length > 0) {
        this.branchTablesComponent.validateAllBranchTables();
      }
    }, 100); // Increased timeout to ensure component is fully initialized
  }

  /**
   * Handle branch table sync event - just log for visibility
   */
  onBranchTableSynced(event: {oldId: string, newTable: BranchTableViewModel}): void {
  }

  /**
   * Handle request to remove a branch table - called by child component
   */
  onRemoveBranchTable(index: number): void {

    if (index >= 0 && index < this.branchTableViewModelList.length) {
      const removedTable = this.branchTableViewModelList[index];
      const notesToCheck: CommonNoteViewModel[] = [];

      if (removedTable.commonNotes && removedTable.commonNotes.length > 0) {
        notesToCheck.push(...removedTable.commonNotes);
      }

      if (removedTable.relationCommonNotes && removedTable.relationCommonNotes.length > 0) {
        notesToCheck.push(...removedTable.relationCommonNotes);
      }


      this.branchTableViewModelList.splice(index, 1);
      this.branchTableViewModelList = [...this.branchTableViewModelList];
      if (this.currentPipeClassViewModel) {
        this.currentPipeClassViewModel.branchTables = this.branchTableViewModelList;
      }

      if (notesToCheck.length > 0) {
        this.removeNotesFromPipeClassIfOrphan(notesToCheck);
      }

      this.updateObservableDataSource();
      this.forceUIUpdate();
    }
  }

  onBranchTableRemoved(removedTable: BranchTableViewModel): void {
    this.cleanupCommonNotesForRemovedTable(removedTable);
  }

  /**
   * Clean up common notes when a branch table is removed
   */
  private cleanupCommonNotesForRemovedTable(removedTable: BranchTableViewModel): void {

    if (!removedTable.commonNotes || removedTable.commonNotes.length === 0) {
      return;
    }

    // Get all common note IDs from the removed table
    const removedCommonNoteIds = removedTable.commonNotes.map(note => note.codeId);

    // Check if any remaining branch tables still use these common notes
    const remainingTables = this.branchTableViewModelList.filter(bt => bt.branchTableId !== removedTable.branchTableId);

    removedCommonNoteIds.forEach(noteId => {
      // Check if this common note exists in any remaining branch table
      const isStillUsed = remainingTables.some(table =>
        table.commonNotes && table.commonNotes.some(note => note.codeId === noteId)
      );

      if (!isStillUsed) {
        // Remove from pipe class common notes if it exists
        if (this.currentPipeClassViewModel?.commonNotes) {
          const pipeClassIndex = this.currentPipeClassViewModel.commonNotes.findIndex(note => note?.codeId === noteId);
          if (pipeClassIndex > -1) {
            this.currentPipeClassViewModel.commonNotes.splice(pipeClassIndex, 1);
            this.commonNotesVersion++; // Trigger UI refresh
          }
        }
      } else {
      }
    });

  }

  /**
   * Handle save button click from SaveCancelButtonsComponent
   */
  onSaveButtonClicked(tabType: string): void {

    switch (tabType) {
      case 'general-details':
        this.saveGeneralDetails();
        break;
      case 'summary':
        this.saveDraft();
        break;
      case 'components':
        this.saveComponents();
        break;
      case 'valves':
        this.saveValves();
        break;
      case 'notes':
        this.saveNotes();
        break;
      default:
        console.warn(`Unknown tab type for save: ${tabType}`);
        break;
    }
  }

  /**
   * Handle cancel button click from SaveCancelButtonsComponent
   */
  onCancelButtonClicked(tabType: string): void {

    switch (tabType) {
      case 'general-details':
        this.cancelGeneralDetails();
        break;
      case 'summary':
        this.cancelSummary();
        break;
      case 'components':
        this.cancelComponents();
        break;
      case 'valves':
        this.cancelValves();
        break;
      case 'notes':
        this.cancelNotes();
        break;
      default:
        console.warn(`Unknown tab type for cancel: ${tabType}`);
        break;
    }
  }

  /**
   * Save changes specific to components tab
   */
  private saveComponents(): void {
    // TODO: Implement components-specific save API call
    // For now, use the general save draft functionality
    this.performSaveDraft();
  }

  /**
   * Cancel changes specific to components tab
   */
  private cancelComponents(): void {
    // TODO: Implement components-specific cancel logic
    // Clear only components-related pending changes
    this.pendingChanges.delete('addedComponents');
    this.pendingChanges.delete('removedComponents');
    this.pendingChanges.delete('modifiedComponents');
  }

  /**
   * Save changes specific to valves tab (placeholder)
   */
  private saveValves(): void {
    // TODO: Implement valves-specific save API call
  }

  /**
   * Cancel changes specific to valves tab (placeholder)
   */
  private cancelValves(): void {
    // TODO: Implement valves-specific cancel logic
  }

  // ================= VALVES TAB NEW METHODS =================

  onAddValves(): void {
    if (!this.currentPipeClassViewModel) { return; }
    const existingIds = (this.currentPipeClassViewModel.valves || []).map(v => v.id || '');
    const dialogData: ValveSelectionModalData = {
      existingValveIds: existingIds,
      title: 'add valves to pipe class'
    };
    const dialogRef = this.dialog.open(ValveSelectionModalComponent, {
      width: '95vw',
      maxWidth: '100vw',
      height: '95vh',
      panelClass: 'valve-selection-modal-dialog',
      data: dialogData
    });
    dialogRef.afterClosed().subscribe((selected: ValveViewModel[]) => {
      if (selected && selected.length > 0) {
        this.addSelectedValves(selected);
      }
    });
  }

  private async loadValvesWithComponent(ids: string[]): Promise<ValveViewModel[]> {
    try {
      const valvesWithComponent = await firstValueFrom(
        this.esraPipingAPIClient
          .getValveViewModelsByIds(ids)
          .pipe(
            indicate(this.loadingIndicator$),
            first()
          )
      );

      return valvesWithComponent;

    } catch (err) {
      console.error('Error fetching valves by ids:', err);
      throw err;
    }
  }

  private addSelectedValves(selected: ValveViewModel[]): void {
    if (!this.currentPipeClassViewModel) { return; }
    if (!this.currentPipeClassViewModel.valves) {
      this.currentPipeClassViewModel.valves = [];
    }
    const existingIds = new Set(this.currentPipeClassViewModel.valves.map(v => v.id));
    const newValves: ValveViewModel[] = [];
    let displayOrderStart = (this.currentPipeClassViewModel.valves.length || 0) + 1;
    // Determine current shared valve notes (all valves share) to propagate to new ones
    const existingValves = this.currentPipeClassViewModel.valves;
    const currentShared: CommonNoteViewModel[] = this.computeSharedValveNotes(existingValves);

    this.loadValvesWithComponent(selected.map(s => s.id)).then(valvesWithComponents => {
      selected.forEach(v => {
      if (!existingIds.has(v.id)) {

        const valveWithComponent = valvesWithComponents.find(vc => vc.id === v.id);
        if(!valveWithComponent) throw new Error('Valve component not found');

        // Clone to decouple from modal arrays
        const clone = new ValveViewModel({ ...valveWithComponent });
        clone.displayOrder = displayOrderStart++;
        if (!clone.commonNotes) { clone.commonNotes = []; }
        // If there are shared notes across existing valves, apply them to the new one to maintain group-level consistency
        if (currentShared.length > 0) {
          const toAdd = currentShared.filter(sn => !clone.commonNotes!.some(x => x.noteId === sn.noteId && x.codeId === sn.codeId));
          clone.commonNotes!.push(...toAdd.map(n => new CommonNoteViewModel({
            noteId: n.noteId,
            codeId: n.codeId,
            note: n.note,
            isActive: n.isActive,
            isArchived: n.isArchived,
            isReferenced: n.isReferenced,
            updated: n.updated ? new Date(n.updated) : undefined
          })));
        }
        (clone as any).__isNew = true;
        this.currentPipeClassViewModel!.valves.push(clone);
        newValves.push(clone);
      }
    });
    if (newValves.length === 0) { return; }

    // SYNC: Also add valves to componentsInPipeClass
    this.syncAddValvesToComponents(newValves);

    // Normalize valve displayOrder (0-based sequential) to avoid gaps or conflicts
    this.reorderValveDisplayOrders();

    // Force array reference change to trigger change detection
    this.currentPipeClassViewModel!.valves = [...this.currentPipeClassViewModel!.valves];

    // Update observable AFTER valves have been added
    this.updateObservableDataSource();
    this.reorderComponentsByGroupAndDisplayOrder();

    this.forceUIUpdate();
    this.onDataChange({
      field: 'addedValves',
      value: newValves.map(v => ({ id: v.id })),
      section: 'valves'
    });
    // Add propagated shared notes (if any) from existing valves to global list, plus any unique notes new valves might have
    const aggregatedNewNotes: CommonNoteViewModel[] = [];
    newValves.forEach(v => {
      (v.commonNotes || []).forEach(n => aggregatedNewNotes.push(n));
    });
    this.addNotesToPipeClassCommon(aggregatedNewNotes, 'valve');
    });
  }

  private computeSharedValveNotes(valves: ValveViewModel[] | undefined): CommonNoteViewModel[] {
    if (!valves || valves.length === 0) return [];
    const first = valves[0].commonNotes || [];
    if (valves.length === 1) return first;
    return first.filter(n => valves.every(v => (v.commonNotes || []).some(x => x.noteId === n.noteId && x.codeId === n.codeId)));
  }

  onEditValveNotes(valve: ValveViewModel): void {
    this.openValveNotesModal(valve);
  }



  private openValveNotesModal(valve: ValveViewModel): void {
    const existingNotes = (valve.commonNotes || []).map(note => ({
      id: note.noteId || '',
      code: note.codeId || '',
      note: note.note || '',
      category: 'Common',
      selected: true
    }));

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      data: {
        title: `Notes for Valve: ${valve.tag}`,
        selectedNotes: existingNotes,
        availableNotes: []
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) { // Handle both empty array and filled array, but not undefined (cancelled)
        // Store previous notes for comparison
        const previousNotes = (valve.commonNotes || []).map(note => ({
          id: note.noteId || '',
          code: note.codeId || '',
          note: note.note || ''
        }));


        const newNotes = result.map(n => new CommonNoteViewModel({
          noteId: n.id || n.noteId || '',
          codeId: n.code || n.codeId || '',
          note: n.note || n.noteText || '',
          isActive: true,
          updated: new Date(),
          isArchived: false,
          isReferenced: false
        }));

        // Update valve notes
        valve.commonNotes = newNotes;

        // Mark valve as modified and assign current revision
        valve.changed = true;
        (valve as any).__isModified = true;

        // ALWAYS assign current revision when modified (overwrite previous revision)
        if (this.currentPipeClassViewModel?.fromRevision?.name) {
          if (!valve.fromRevision) {
            valve.fromRevision = {} as any;
          }
          valve.fromRevision.name = this.currentPipeClassViewModel.fromRevision.name;
        }

        const valveComponent = valve.componentInPipeClassId
          ? this.currentPipeClassViewModel.componentsInPipeClass.find(c => c.id === valve.componentInPipeClassId)
          : this.currentPipeClassViewModel.componentsInPipeClass.find(c => c.component.componentId === valve.components[0].componentId);
        if(valveComponent) {
          valveComponent.commonNotes = newNotes;
          valveComponent.lastUpdatedRevision = this.currentPipeClassViewModel.fromRevision?.name || '';
          valveComponent.changed = true;
        }

        // Handle bidirectional synchronization
        // Find added notes
        const addedNotes = result.filter(newNote =>
          !previousNotes.some(oldNote => oldNote.id === newNote.id && oldNote.code === newNote.code)
        );

        // Find removed notes
        const removedNotes = previousNotes.filter(oldNote =>
          !result.some(newNote => newNote.id === oldNote.id && newNote.code === oldNote.code)
        );

        if (addedNotes.length > 0) {
          const addedAsCommonNotes = addedNotes.map(note => new CommonNoteViewModel({
            noteId: note.id,
            codeId: note.code,
            note: note.note
          }));
          this.addNotesToPipeClassCommon(addedAsCommonNotes, `valve-${valve.tag}`);
        }

        if (removedNotes.length > 0) {
          const removedAsCommonNotes = removedNotes.map(note => new CommonNoteViewModel({
            noteId: note.id,
            codeId: note.code,
            note: note.note
          }));
          this.removeNotesFromPipeClassIfOrphan(removedAsCommonNotes);
        }

        this.updateObservableDataSource();
        this.forceUIUpdate();

        this.onDataChange({
          field: 'valveNotes',
          value: { valveId: valve.id, notes: newNotes },
          section: 'valves'
        });
      } else {
      }
    });
  }

  onRemoveValve(valve: ValveViewModel): void {
    if (!this.currentPipeClassViewModel?.valves) { return; }
    const idx = this.currentPipeClassViewModel.valves.findIndex(v => v.id === valve.id);
    if (idx === -1) { return; }

    const removedValve = this.currentPipeClassViewModel.valves[idx];
    const removedNotes = removedValve.commonNotes || [];
    this.currentPipeClassViewModel.valves.splice(idx, 1);
    this.syncRemoveValveFromComponents(valve);

    this.removeNotesFromPipeClassIfOrphan(removedNotes);
    this.commonNotesVersion++;
    this.updateObservableDataSource();
    this.forceUIUpdate();
    this.onDataChange({
      field: 'removedValve',
      value: { id: valve.id },
      section: 'valves'
    });
  }

  onEditValveGroupNotes(): void {
    // Get valve group notes - can exist even if no valves are present
    const valves = this.currentPipeClassViewModel?.valves || [];
    let shared: CommonNoteViewModel[] = [];

    if (valves.length > 0) {
      // Compute shared notes across all valves (normal case)
      const firstNotes = valves[0].commonNotes || [];
      shared = firstNotes.filter(n => valves.every(v => (v.commonNotes || []).some(x => x.noteId === n.noteId && x.codeId === n.codeId)));
    } else {
      // No valves present - check if there are orphaned valve group notes
      // These would be stored at the pipe class level or in a separate valve group notes collection
      // For now, allow opening with empty notes so user can manage orphaned notes
      shared = [];
    }


    const existingNotes = shared.map(note => ({
      id: note.noteId || '',
      code: note.codeId || '',
      note: note.note || '',
      category: 'Common',
      selected: true
    }));

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      data: { title: 'Valve Group Notes', selectedNotes: existingNotes, availableNotes: [] }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && Array.isArray(result)) { // Handle both empty array and filled array, but not undefined (cancelled)
        // Store previous notes for comparison
        const previousNotes = shared.map(note => ({
          id: note.noteId || '',
          code: note.codeId || '',
          note: note.note || ''
        }));

        // Map to CommonNoteViewModel
        const selectedCommon = result.map(n => new CommonNoteViewModel({
          noteId: n.id || n.noteId || '',
          codeId: n.code || n.codeId || '',
          note: n.note || n.noteText || '',
          isActive: true,
          updated: new Date(),
          isArchived: false,
          isReferenced: false
        }));

        // Determine add/remove set relative to current shared
        const toAdd = selectedCommon.filter(sn => !shared.some(c => c.noteId === sn.noteId && c.codeId === sn.codeId));
        const toRemove = shared.filter(c => !selectedCommon.some(sn => sn.noteId === c.noteId && sn.codeId === c.codeId));

        // Apply changes to all valves
        valves.forEach((v, index) => {
          if (!v.commonNotes) v.commonNotes = [];
          toAdd.forEach(a => {
            if (!v.commonNotes!.some(x => x.noteId === a.noteId && x.codeId === a.codeId)) {
              v.commonNotes!.push(a);
            }
          });
          v.commonNotes = v.commonNotes.filter(x => !toRemove.some(r => r.noteId === x.noteId && r.codeId === x.codeId));

          // Mark valve as modified if notes were added or removed
          if (toAdd.length > 0 || toRemove.length > 0) {
            (v as any).__isModified = true;

            // ALWAYS assign current revision when modified (overwrite previous revision)
            if (this.currentPipeClassViewModel?.fromRevision?.name) {
              if (!v.fromRevision) {
                v.fromRevision = {} as any;
              }
              v.fromRevision.name = this.currentPipeClassViewModel.fromRevision.name;
            }
          }
        });

        // Handle bidirectional synchronization
        // Find added notes
        const addedNotes = result.filter(newNote =>
          !previousNotes.some(oldNote => oldNote.id === newNote.id && oldNote.code === newNote.code)
        );

        // Find removed notes
        const removedNotes = previousNotes.filter(oldNote =>
          !result.some(newNote => newNote.id === oldNote.id && newNote.code === oldNote.code)
        );

        if (addedNotes.length > 0) {
          this.addNotesToPipeClassCommon(toAdd, 'valve-group');
        }

        if (removedNotes.length > 0) {
          this.removeNotesFromPipeClassIfOrphan(toRemove);
        }
        this.commonNotesVersion++;
        this.updateObservableDataSource();
        this.forceUIUpdate();
        this.cdr.markForCheck();

        this.onDataChange({
          field: 'valveGroupNotes',
          value: selectedCommon,
          section: 'valves'
        });
      }
    });
  }

  /**
   * Save changes specific to notes tab (placeholder)
   */
  private saveNotes(): void {
    // TODO: Implement notes-specific save API call
  }

  // ========== GENERAL DETAILS TAB METHODS ==========

  /**
   * Handle user role changes from General Details tab
   */
  onUserRoleChanged(event: {role: string, users: any[]}): void {

    // Update the current pipe class data with the new user assignments
    if (this.currentPipeClassViewModel) {
      // Store the user role changes for saving later
      this.onDataChange({
        field: `userRole_${event.role}`,
        value: event.users,
        section: 'general-details'
      });
    }
  }

  /**
   * Handle description changes from General Details tab
   */
  onDescriptionChanged(description: string): void {

    // Update the current pipe class data
    if (this.currentPipeClassViewModel?.fromRevision) {
      this.currentPipeClassViewModel.fromRevision.description = description;

      // Store the description change for saving later
      this.onDataChange({
        field: 'revisionDescription',
        value: description,
        section: 'general-details'
      });
    }
  }

  /**
   * Save changes specific to general details tab.
   * Uses PUT /Revision/{id}/savedraft so roles and status are persisted atomically.
   */
  private saveGeneralDetails(): void {
    if (!this.currentPipeClassViewModel?.fromRevision?.id) {
      return;
    }

    const revision = this.currentPipeClassViewModel.fromRevision;
    const members = this.generalDetailsComponent ? this.generalDetailsComponent.getCurrentMembers() : [];

    // Members are saved via updateRevisionMembers — changeRevisionStatus no longer accepts a body.
    this.esraPipingAPIClient.updateRevisionMembers(revision.id, members)
      .pipe(first())
      .subscribe({
        next: (updatedRevision) => {
          if (updatedRevision?.roles?.length && this.generalDetailsComponent) {
            this.generalDetailsComponent.updateRolesFromResponse(updatedRevision.roles);
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error saving general details:', error);
        }
      });
  }

  /**
   * Cancel changes specific to general details tab
   */
  private cancelGeneralDetails(): void {
    // TODO: Implement general-details-specific cancel logic
    // Clear only general-details-related pending changes
    this.pendingChanges.delete('revisionDescription');
    this.pendingChanges.delete('userRole_author');
    this.pendingChanges.delete('userRole_reviewer');
    this.pendingChanges.delete('userRole_contributor');
  }

  /**
   * Cancel changes specific to notes tab (placeholder)
   */
  private cancelNotes(): void {
    // TODO: Implement notes-specific cancel logic
  }

  /**
   * Cancel changes specific to summary tab (placeholder)
   */
  private cancelSummary(): void {
    // TODO: Implement summary-specific cancel logic
  }

  /**
   * Handle notes added to branch tables
   */
  onBranchTableNotesAdded(event: {notes: CommonNoteViewModel[], origin: string}): void {
    this.addNotesToPipeClassCommon(event.notes, event.origin);
  }

  /**
   * Handle notes removed from branch tables
   */
  onBranchTableNotesRemoved(event: {notes: CommonNoteViewModel[], origin: string}): void {
    this.removeNotesFromPipeClassIfOrphan(event.notes);
  }

  // New methods for valve drag-and-drop and NPS editing
  onReorderValves(reorderedValves: ValveViewModel[]): void {
    if (!this.currentPipeClassViewModel) { return; }

    // Update the pipe class with the new order
    this.currentPipeClassViewModel.valves = [...reorderedValves];

    // Sync displayOrder for matching components using componentInPipeClassId for exact match
    const components = this.currentPipeClassViewModel.componentsInPipeClass;
    reorderedValves.forEach(valve => {
      const component = valve.componentInPipeClassId
        ? components.find(c => c.id === valve.componentInPipeClassId)
        : components.find(c => c.component?.componentId === valve.components[0]?.componentId);
      if (component) {
        component.displayOrder = valve.displayOrder;
      }
    });
    this.reorderComponentsByGroupAndDisplayOrder();

    this.onDataChange({ field: 'valves', value: reorderedValves, section: 'valves' });
  }

  onUpdateValveNps(event: {valve: ValveViewModel, newNps: string, index: number, componentsInPipeClassId: string}): void {
    if (!this.currentPipeClassViewModel) { return; }

    // Use the emitted index to target the exact valve in the array,
    // since multiple valves can share the same id
    const valveIndex = event.index;
    if (valveIndex !== undefined && valveIndex >= 0 && this.currentPipeClassViewModel.valves) {
      const valve = this.currentPipeClassViewModel.valves[valveIndex];
      valve.nps = event.newNps;

      // Mark valve as modified and assign current revision
      valve.changed = true;
      (valve as any).__isModified = true;

      // ALWAYS assign current revision object when modified (not just the name)
      if (this.currentPipeClassViewModel?.fromRevision) {
        valve.fromRevision = this.currentPipeClassViewModel.fromRevision;
      }

      console.log('[onUpdateValveNps] Valve marked as modified:', {
        valveTag: valve.tag,
        __isModified: (valve as any).__isModified,
        fromRevisionName: valve.fromRevision?.name,
        fromRevisionId: valve.fromRevision?.id,
        newNps: event.newNps
      });

      const componentId = valve.components[0].componentId;
      // SYNC: Update in componentsInPipeClass
      this.syncValveNpsToComponents(componentId, event.newNps, event.componentsInPipeClassId);

      this.onDataChange({ field: 'nps', value: event.newNps, section: 'valves' });
    }
  }

  onUpdateValveRatings(event: {valve: ValveViewModel, newRatings: string, index: number, componentsInPipeClassId: string}): void {
    if (!this.currentPipeClassViewModel) { return; }

    // Use the emitted index to target the exact valve in the array,
    // since multiple valves can share the same id
    const valveIndex = event.index;
    if (valveIndex !== undefined && valveIndex >= 0 && this.currentPipeClassViewModel.valves) {
      const valve = this.currentPipeClassViewModel.valves[valveIndex];
      valve.rating = event.newRatings;

      // Mark valve as modified and assign current revision
      valve.changed = true;
      (valve as any).__isModified = true;

      // ALWAYS assign current revision object when modified (not just the name)
      if (this.currentPipeClassViewModel?.fromRevision) {
        valve.fromRevision = this.currentPipeClassViewModel.fromRevision;
      }

      // CRITICAL: Also update valve.components[0] to maintain consistency
      if (valve.components && valve.components.length > 0) {
        valve.components[0].schRat = event.newRatings;
      }

      if (valve.components && valve.components.length > 0 && valve.components[0].componentId) {
        const componentId = valve.components[0].componentId;

        this.syncValveRatingToComponents(componentId, event.newRatings, valve.ends, event.componentsInPipeClassId);
      }

      this.onDataChange({ field: 'rating', value: event.newRatings, section: 'valves' });
    }
  }

  onUpdateValveEnds(event: {valve: ValveViewModel, newEnds: string, index: number, componentsInPipeClassId: string}): void {
    if (!this.currentPipeClassViewModel) { return; }

    // Use the emitted index to target the exact valve in the array,
    // since multiple valves can share the same id
    const valveIndex = event.index;
    if (valveIndex !== undefined && valveIndex >= 0 && this.currentPipeClassViewModel.valves) {
      const valve = this.currentPipeClassViewModel.valves[valveIndex];
      valve.ends = event.newEnds;

      // Mark valve as modified and assign current revision
      valve.changed = true;
      (valve as any).__isModified = true;

      // ALWAYS assign current revision object when modified (not just the name)
      if (this.currentPipeClassViewModel?.fromRevision) {
        valve.fromRevision = this.currentPipeClassViewModel.fromRevision;
      }

      if (valve.components && valve.components.length > 0) {
        valve.components[0].end = event.newEnds;
      }

      if (valve.components && valve.components.length > 0 && valve.components[0].componentId) {
        const componentId = valve.components[0].componentId;
        this.syncValveEndsToComponents(componentId, event.newEnds, valve.rating, event.componentsInPipeClassId);
      }

      this.onDataChange({ field: 'ends', value: event.newEnds, section: 'valves' });
    }
  }
  onUpdateValveDescription(event: {valve: ValveViewModel, newDescription: string, index: number, componentsInPipeClassId: string}): void {
    if (!this.currentPipeClassViewModel) { return; }

    // Use the emitted index to target the exact valve in the array,
    // since multiple valves can share the same id
    const valveIndex = event.index;
    if (valveIndex !== undefined && valveIndex >= 0 && this.currentPipeClassViewModel.valves) {
      const valve = this.currentPipeClassViewModel.valves[valveIndex];
      valve.description = event.newDescription;

      // Mark valve as modified and assign current revision
      valve.changed = true;
      (valve as any).__isModified = true;

      // ALWAYS assign current revision object when modified (not just the name)
      if (this.currentPipeClassViewModel?.fromRevision) {
        valve.fromRevision = this.currentPipeClassViewModel.fromRevision;
      }

      // Backend's ResolveValveComponentInPipeClass resolves the exact row being edited
      // primarily via ComponentInPipeClassId, falling back to ComponentId + DisplayOrder
      // only when it is missing. Guarantee both are set and consistent with the row the
      // user is actually editing before this valve is sent on save.
      if (event.componentsInPipeClassId) {
        valve.componentInPipeClassId = event.componentsInPipeClassId;
      }
      valve.displayOrder = valveIndex;

      if (valve.components && valve.components.length > 0) {
        valve.components[0].description = event.newDescription;
      }

      if (valve.components && valve.components.length > 0 && valve.components[0].componentId) {
        this.syncUpdateValveInComponents(valve, event.componentsInPipeClassId);
      }

      this.onDataChange({ field: 'description', value: event.newDescription, section: 'valves' });
    }
  }

  // ========== COMMENTS MANAGEMENT ==========

  /**
   * Load all comments for the current pipe class
   */
  private loadAllComments(): void {
    if (!this.currentPipeClassViewModel) return;

    const revisionId = this.currentPipeClassViewModel.fromRevision?.id?.toString() || '1';

    this.commentsLoading$.next(true);

    this.commentsService.getAllComments(this.pipeClassId, revisionId)
      .pipe(
        tap(() => this.commentsLoading$.next(false))
      )
      .subscribe({
        next: (comments) => {
          this.summaryComments = comments.summary || [];
          this.componentComments = comments.components || [];
          this.valveComments = comments.valves || [];
          this.branchTableComments = comments.branchtables || [];
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading comments:', error);
          this.commentsLoading$.next(false);
          // Initialize with empty arrays on error
          this.summaryComments = [];
          this.componentComments = [];
          this.valveComments = [];
          this.branchTableComments = [];
        }
      });
  }

  /**
   * Save comments for a specific tab
   */
  saveTabComments(contextType: 'summary' | 'components' | 'valves' | 'branchtables'): void {
    if (!this.currentPipeClassViewModel) return;

    const revisionId = this.currentPipeClassViewModel.fromRevision?.id?.toString() || '1';
    let comments: CommentViewModel[] = [];

    switch (contextType) {
      case 'summary':
        comments = this.summaryComments;
        break;
      case 'components':
        comments = this.componentComments;
        break;
      case 'valves':
        comments = this.valveComments;
        break;
      case 'branchtables':
        comments = this.branchTableComments;
        break;
    }

    this.commentsSaving$.next(true);

    this.commentsService.saveCommentsForContext(this.pipeClassId, revisionId, contextType, comments)
      .pipe(
        tap(() => this.commentsSaving$.next(false))
      )
      .subscribe({
        next: (savedComments) => {
          // Update the local comments with the saved ones
          switch (contextType) {
            case 'summary':
              this.summaryComments = savedComments;
              break;
            case 'components':
              this.componentComments = savedComments;
              break;
            case 'valves':
              this.valveComments = savedComments;
              break;
            case 'branchtables':
              this.branchTableComments = savedComments;
              break;
          }
          this.cdr.detectChanges();
          console.log(`${contextType} comments saved successfully`);
        },
        error: (error) => {
          console.error(`Error saving ${contextType} comments:`, error);
          this.commentsSaving$.next(false);
        }
      });
  }

  /**
   * Save all comments across all tabs
   */
  saveAllComments(): void {
    if (!this.currentPipeClassViewModel) return;

    const revisionId = this.currentPipeClassViewModel.fromRevision?.id?.toString() || '1';

    const request: SaveAllCommentsRequest = {
      pipeClassId: this.pipeClassId,
      revisionId: revisionId,
      summaryComments: this.summaryComments,
      componentComments: this.componentComments,
      valveComments: this.valveComments,
      branchTableComments: this.branchTableComments
    };

    this.commentsSaving$.next(true);

    this.commentsService.saveAllComments(request)
      .pipe(
        tap(() => this.commentsSaving$.next(false))
      )
      .subscribe({
        next: (savedComments) => {
          // Update all local comment arrays with saved ones
          this.summaryComments = savedComments.summary;
          this.componentComments = savedComments.components;
          this.valveComments = savedComments.valves;
          this.branchTableComments = savedComments.branchtables;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error saving all comments:', error);
          this.commentsSaving$.next(false);
        }
      });
  }

  /**
   * Event handlers for child components
   */
  onSummaryCommentsChanged(comments: CommentViewModel[]): void {
    this.summaryComments = comments;
  }

  /**
   * Handle auto-save request from summary comments
   */
  onSummaryCommentsSaveRequested(): void {
    this.saveTabComments('summary');
  }

  /**
   * Handle auto-save request from component comments
   */
  onComponentCommentsSaveRequested(): void {
    this.saveTabComments('components');
  }

  /**
   * Handle auto-save request from valve comments
   */
  onValveCommentsSaveRequested(): void {
    this.saveTabComments('valves');
  }

  /**
   * Handle auto-save request from branch table comments
   */
  onBranchTableCommentsSaveRequested(): void {
    this.saveTabComments('branchtables');
  }

  onComponentCommentsChanged(comments: CommentViewModel[]): void {
    this.componentComments = comments;
  }

  onValveCommentsChanged(comments: CommentViewModel[]): void {
    this.valveComments = comments;
  }

  onBranchTableCommentsChanged(comments: CommentViewModel[]): void {
    this.branchTableComments = comments;
  }

  // Valve-Component Synchronization Methods
  /**
   * Creates one `ComponentsInPipeClassViewModel` row per valve being added, even when multiple
   * valves reference the same catalog `componentId` (e.g. the same valve catalog entry used
   * twice within the same pipe class, or across pipe classes in a release). Each valve row must
   * be uniquely identified by its own `componentInPipeClassId` -- reusing/matching an existing
   * row by `componentId` alone causes two valve rows to resolve to the same join-table id
   * ("mirror" bug), so every valve added here always gets its own new row and its own object
   * instance, then that id is written back onto the valve immediately.
   */
  private syncAddValvesToComponents(valves: ValveViewModel[]): void {

    if (!this.currentPipeClassViewModel?.componentsInPipeClass) {
      return;
    }

    let added = 0;
    valves.forEach(valve => {
      const sourceComponent = valve.components && valve.components[0];
      const endsValue = sourceComponent?.end || valve.ends;
      const ratingValue = sourceComponent?.schRat || valve.rating;

      // Always create a fresh ComponentViewModel/ComponentsInPipeClassViewModel instance for
      // this specific valve row -- never reuse/share an existing row's object reference, even
      // when another row already has the same catalog componentId.
      const componentFromValve = new ComponentViewModel({
        componentClassificationId: sourceComponent?.componentClassificationId || this.generateGuid(),
        componentId: sourceComponent?.componentId,
        nps: valve.nps,
        type: 'Valve',
        group: 'Valves',
        description: valve.description,
        end: endsValue,
        schRat: ratingValue,
        code: valve.tag,
        isActive: true
      });

      const componentInPipeClass = new ComponentsInPipeClassViewModel({
        id: this.generateGuid(),
        component: componentFromValve,
        displayOrder: this.currentPipeClassViewModel!.componentsInPipeClass!.length
      });

      this.currentPipeClassViewModel!.componentsInPipeClass!.push(componentInPipeClass);
      // Bind the valve to its own, unambiguous row immediately so downstream saves/edits never
      // fall back to the ambiguous componentId-based match.
      valve.componentInPipeClassId = componentInPipeClass.id;
      added++;
    });

    this.cdr.markForCheck();
  }


  private syncRemoveValveFromComponents(valve: ValveViewModel): void {
    if (!this.currentPipeClassViewModel?.componentsInPipeClass || !valve.id) {
      return;
    }

    let index = this.currentPipeClassViewModel.componentsInPipeClass
      .findIndex(comp => comp.component?.componentId === valve.components[0].componentId);

    if (index === -1) {
      index = this.currentPipeClassViewModel.componentsInPipeClass.findIndex(comp =>
        comp.component?.group === 'Valves' &&
        comp.component?.description === valve.description &&
        comp.component?.nps === valve.nps &&
        comp.component?.end === valve.ends
      );
    }

    if (index > -1) {
      const removedComponent = this.currentPipeClassViewModel.componentsInPipeClass[index];
      const componentNotes = removedComponent.commonNotes || [];
      this.currentPipeClassViewModel.componentsInPipeClass.splice(index, 1);
      const valveNotes = valve.commonNotes || [];
      const uniqueComponentNotes = componentNotes.filter(cn =>
        !valveNotes.some(vn => vn.noteId === cn.noteId && vn.codeId === cn.codeId)
      );

      if (uniqueComponentNotes.length > 0) {
        this.removeNotesFromPipeClassIfOrphan(uniqueComponentNotes);
        this.commonNotesVersion++;
      }
      this.cdr.markForCheck();
    }
  }

  private syncUpdateValveInComponents(valve: ValveViewModel, componentsInPipeClassId?: string): void {

    if (!this.currentPipeClassViewModel?.componentsInPipeClass || !valve.id) {
      return;
    }

    // Prefer the unambiguous join-table id (same convention as syncValveNpsToComponents /
    // syncValveRatingToComponents / syncValveEndsToComponents). Matching by componentId alone
    // is ambiguous when the same catalog valve appears in multiple rows within the pipe class.
    const resolvedId: string | undefined = componentsInPipeClassId ?? valve.componentInPipeClassId;
    const componentIndex = resolvedId
      ? this.currentPipeClassViewModel.componentsInPipeClass.findIndex(comp => comp.id === resolvedId)
      : this.currentPipeClassViewModel.componentsInPipeClass
        .findIndex(comp => comp.component?.componentId === valve.components[0].componentId);

    if (componentIndex > -1) {
      // Update component properties from valve
      const componentInPipeClass = this.currentPipeClassViewModel.componentsInPipeClass[componentIndex];
      if (componentInPipeClass.component) {
        componentInPipeClass.component.nps = valve.nps;
        componentInPipeClass.component.description = valve.description;
        componentInPipeClass.component.end = valve.ends;
        componentInPipeClass.component.schRat = valve.rating;
      }

      this.cdr.markForCheck();
    }
  }

  private syncValveNpsToComponents(componentId: string, newNps: string, componentsInPipeClassId?: string): void {
    if (!this.currentPipeClassViewModel?.componentsInPipeClass) {
      return;
    }

    // Use componentsInPipeClassId for exact match when available, fall back to componentId
    const componentIndex = componentsInPipeClassId
      ? this.currentPipeClassViewModel.componentsInPipeClass.findIndex(comp => comp.id === componentsInPipeClassId)
      : this.currentPipeClassViewModel.componentsInPipeClass.findIndex(comp => comp.component?.componentId === componentId);

    if (componentIndex > -1 && this.currentPipeClassViewModel.componentsInPipeClass[componentIndex].component) {
      const comp = this.currentPipeClassViewModel.componentsInPipeClass[componentIndex];
      comp.component!.nps = newNps;

      const trimmedValue = newNps.trim();
      const parsedRange = this._npsValidationService.parseNpsRange(trimmedValue);
      if (parsedRange) {
        comp.component!.npsFrom = parsedRange.npsFrom;
        comp.component!.npsTo = parsedRange.npsTo;
      }

      comp.lastUpdatedRevision = this.currentPipeClassViewModel.fromRevision?.name || '';
      comp.changed = true;
      this.cdr.markForCheck();
    }
  }

  private syncValveRatingToComponents(componentId: string, newRatings: string, currentEnds?: string, componentsInPipeClassId?: string): void {
    if (!this.currentPipeClassViewModel?.componentsInPipeClass) {
      console.warn('⚠︝ [SyncRating] No componentsInPipeClass found');
      return;
    }

    // Use componentsInPipeClassId for exact match when available, fall back to componentId
    const componentIndex = componentsInPipeClassId
      ? this.currentPipeClassViewModel.componentsInPipeClass.findIndex(comp => comp.id === componentsInPipeClassId)
      : this.currentPipeClassViewModel.componentsInPipeClass.findIndex(comp => comp.component?.componentId === componentId);

    if (componentIndex > -1 && this.currentPipeClassViewModel.componentsInPipeClass[componentIndex].component) {
      const comp = this.currentPipeClassViewModel.componentsInPipeClass[componentIndex];
      comp.component!.schRat = newRatings;

      if (currentEnds !== undefined && currentEnds !== null) {
        comp.component!.end = currentEnds;
      }

      comp.lastUpdatedRevision = this.currentPipeClassViewModel.fromRevision?.name || '';
      comp.changed = true;
      this.cdr.markForCheck();
    }
  }

  private syncValveEndsToComponents(componentId: string, newEnds: string, currentRating?: string, componentsInPipeClassId?: string): void {
    if (!this.currentPipeClassViewModel?.componentsInPipeClass) {
      return;
    }

    // Use componentsInPipeClassId for exact match when available, fall back to componentId
    const componentIndex = componentsInPipeClassId
      ? this.currentPipeClassViewModel.componentsInPipeClass.findIndex(comp => comp.id === componentsInPipeClassId)
      : this.currentPipeClassViewModel.componentsInPipeClass.findIndex(comp => comp.component?.componentId === componentId);

    if (componentIndex > -1 && this.currentPipeClassViewModel.componentsInPipeClass[componentIndex].component) {
      const comp = this.currentPipeClassViewModel.componentsInPipeClass[componentIndex];
      comp.component!.end = newEnds;
      if (currentRating !== undefined && currentRating !== null) {
        comp.component!.schRat = currentRating;
      }
      comp.lastUpdatedRevision = this.currentPipeClassViewModel.fromRevision?.name || '';
      comp.changed = true;
      this.cdr.markForCheck();
    }
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Normalize valve displayOrder values to be 0-based sequential.
   * Also syncs the displayOrder to the matching ComponentsInPipeClassViewModel entry.
   */
  private reorderValveDisplayOrders(): void {
    if (!this.currentPipeClassViewModel?.valves || this.currentPipeClassViewModel.valves.length === 0) {
      return;
    }

    // Sort by current displayOrder first to preserve existing order
    this.currentPipeClassViewModel.valves.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    // Reassign 0-based sequential displayOrder
    this.currentPipeClassViewModel.valves.forEach((valve, index) => {
      valve.displayOrder = index;

      // Sync to matching ComponentsInPipeClassViewModel entry
      if (this.currentPipeClassViewModel?.componentsInPipeClass && valve.components?.length > 0) {
        const component: ComponentsInPipeClassViewModel | undefined = valve.componentInPipeClassId
          ? this.currentPipeClassViewModel.componentsInPipeClass.find(c => c.id === valve.componentInPipeClassId)
          : this.currentPipeClassViewModel.componentsInPipeClass.find(c => c.component?.componentId === valve.components[0]?.componentId);
        if (component) {
          component.displayOrder = index;
        }
      }
    });
  }

  private reorderComponentsByGroupAndDisplayOrder(){
    if (!this.currentPipeClassViewModel?.componentsInPipeClass) {
      return;
    }
    const components = this.currentPipeClassViewModel.componentsInPipeClass;
    // Sort by group first, then by current displayOrder
    components.sort((a, b) => {
      const groupIndexA = this.orderedComponentGroups.indexOf(a.component.group);
      const groupIndexB = this.orderedComponentGroups.indexOf(b.component.group);

      if (groupIndexA !== groupIndexB) {
        return groupIndexA - groupIndexB;
      }

      return a.displayOrder - b.displayOrder;
    });

    // Update displayOrder sequentially
    components.forEach((comp, index) => {
      comp.displayOrder = index;
    });
  }

  /**
   * Opens the Clone PipeClass dialog for the currently loaded pipe class.
   * Pre-fills Business Unit and Application from the current pipe class data.
   * Opens from both edit mode and read-only mode (PipeClass Detail entry point).
   */
  openClonePipeClassDialog(): void {
    const pipeClassId: string | undefined = this.currentPipeClassViewModel?.id;
    if (!pipeClassId) { return; }

    const dialogData: ClonePipeClassDialogData = {
      sourcePipeClassId: pipeClassId,
      prefillBusinessUnit: this.currentPipeClassViewModel?.businessUnits?.[0],
      prefillApplication:  this.currentPipeClassViewModel?.application,
      // Show the draft info message only when the user arrived from the revisions dashboard,
      // since that context has an active revision/release association.
      showDraftInfoMessage: this.isInRevisionWorkflow,
    };

    this.dialog.open(ClonePipeClassDialogComponent, {
      width: '650px',
      disableClose: false,
      data: dialogData,
    });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
    if (this.routeParamsSubscription) {
      this.routeParamsSubscription.unsubscribe();
    }

    // Navigation origin is tracked via history.state (router navigation state), not session
    // storage, so no cleanup is needed here. Session storage is only used as a legacy fallback
    // and is consumed/cleared the moment it is read in checkIfCameFromRevisionsDashboard().

    // Ensure edit mode is disabled when component is destroyed
    this.canEditSubject.next(false);
  }
}

